import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  ArrowLeft,
  BadgeCheck,
  Coins,
  Sparkles,
  Play,
  RotateCw,
  Bot,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Trophy,
  Skull,
  Swords,
  Copy,
  BookOpen,
} from 'lucide-react';
import BelotArmenianLocalLobby from './BelotArmenianLocalLobby';
import { AudioProvider, useAudio } from '../poker-ui/AudioProvider';
import { GameIndustrialHeader } from '../poker-ui/GameIndustrialHeader';
import { ArcadeModeBackdrop } from '../poker-ui/ArcadeModeBackdrop';
import { IndustrialSeat } from '../poker-ui/IndustrialSeat';
import { PokerTableSurface } from '../poker-ui/PokerTableSurface';
import { PlayingCard } from '../poker-ui/PlayingCard';
import {
  buildBelotFourMobileStackSlots,
  buildEllipticSeatSlots,
  getSeatDensityPreset,
  getSeatArenaTuning,
} from '../poker-ui/seatLayout';
import { seatDisplayPercents, SEAT_OFFSET_STORAGE } from '../poker-ui/seatLayoutOffsets';
import { cn } from '../poker-ui/utils/cn';
import { getApiBaseUrl } from '../lib/apiBaseUrl';
import { belotCardIdToPlaying, belotMatchCpFromEnginePoints } from '../game/belotArmenianCards';
import { useBelotDealAnimation } from './belot/hooks/useBelotDealAnimation';
import { useBelotSeatLayout } from './belot/hooks/useBelotSeatLayout';
import { BelotDeckTrumpIndicator } from './belot/BelotDeckTrumpIndicator';
import { getTrickCenterDropMetric } from './belot/belotTrickDropMetric';
import { BELOT_FX_Z_TABLE } from './online/belotFxLayers';

function BelotCurrencyToast({ message, kind = 'info', onClose }) {
  const onCloseRef = useRef(onClose);
  const [leaving, setLeaving] = useState(false);
  const [coinImgMissing, setCoinImgMissing] = useState(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const leaveTimer = setTimeout(() => setLeaving(true), 2300);
    const closeTimer = setTimeout(() => onCloseRef.current?.(), 2700);
    return () => {
      clearTimeout(leaveTimer);
      clearTimeout(closeTimer);
    };
  }, []);

  const text = String(message || '');
  const isDebit = kind === 'debit';
  const isCredit = kind === 'credit';
  const isCurrencyToast = isDebit || isCredit;
  const shellClass = isDebit
    ? 'border-red-400/45 from-[#3a141a]/96 via-[#281018]/96 to-[#15080c]/96 shadow-[0_12px_34px_rgba(220,38,38,0.28)]'
    : isCredit
      ? 'border-emerald-300/55 from-[#10351f]/96 via-[#0f2b1b]/96 to-[#0a1a10]/96 shadow-[0_12px_34px_rgba(34,197,94,0.34)]'
      : 'border-cyan-300/45 from-[#10364d]/96 via-[#0e283b]/96 to-[#0a1726]/96 shadow-[0_12px_32px_rgba(34,211,238,0.26)]';
  const iconClass = isDebit
    ? 'border-red-300/35 bg-red-500/18 text-rose-100'
    : isCredit
      ? 'border-emerald-300/45 bg-emerald-500/20 text-yellow-200'
      : 'border-cyan-300/35 bg-cyan-500/15 text-cyan-100';
  const Icon = isCurrencyToast ? Coins : BadgeCheck;
  const coinImgSrc = `${process.env.PUBLIC_URL || ''}/1fishka.png`;
  const toneClass = isDebit ? 'text-rose-100' : isCredit ? 'text-yellow-200' : 'text-cyan-50';
  const shift = leaving ? '-translate-y-1 scale-[0.985] opacity-0' : 'translate-y-0 scale-100 opacity-100';

  return (
    <div className="pointer-events-none fixed left-1/2 top-[max(0.7rem,env(safe-area-inset-top,0px))] z-[130] w-auto max-w-[min(calc(100%-2.25rem),22rem)] -translate-x-1/2">
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border bg-gradient-to-br px-3 py-2 backdrop-blur-md transition-all duration-300 ease-out',
          'animate-[app-toast-in_360ms_cubic-bezier(0.2,0.8,0.2,1)]',
          shellClass,
          shift
        )}
        role={isDebit ? 'alert' : 'status'}
      >
        <span className="pointer-events-none absolute inset-x-0 top-0 h-[40%] bg-gradient-to-b from-white/18 to-transparent" aria-hidden />
        <div className="relative z-[1] flex items-center gap-2.5">
          <span className={cn('inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border', iconClass)}>
            {isCurrencyToast ? (
              !coinImgMissing ? (
                <img
                  src={coinImgSrc}
                  alt=""
                  width={20}
                  height={20}
                  className="h-5 w-5 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]"
                  onError={() => setCoinImgMissing(true)}
                  draggable={false}
                  aria-hidden
                />
              ) : <Icon className="h-4.5 w-4.5" aria-hidden />
            ) : (
              <Icon className="h-4.5 w-4.5" aria-hidden />
            )}
          </span>
          <p className={cn('min-w-0 break-words leading-snug text-[0.95rem] font-black tracking-[0.02em]', toneClass)}>
            {text}
          </p>
        </div>
      </div>
    </div>
  );
}

function ModeFlag({ src, alt }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="relative inline-flex h-6 w-9 shrink-0 overflow-hidden rounded-md border border-white/25 shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_8px_16px_rgba(0,0,0,0.3)]">
      {!broken ? (
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
          draggable={false}
        />
      ) : (
        <span className="flex h-full w-full items-center justify-center bg-white/10 text-[9px] font-bold text-white/70">
          {alt}
        </span>
      )}
      <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/22 via-transparent to-black/10" />
    </span>
  );
}

const TOTAL_PLAYERS = 4;
const HUMAN_PLAYERS = 1;
const ALLOWED_BOT_COUNTS = [TOTAL_PLAYERS - HUMAN_PLAYERS];
const SUIT_LABEL = { C: 'Трефы', D: 'Бубны', H: 'Черви', S: 'Пики', N: 'Без козыря', A: 'Все козыри' };
const CARD_SUIT = { C: '♣', D: '♦', H: '♥', S: '♠' };
const SEAT_LAYOUT_ORDER = [1, 2, 0, 3];
const BELOT_MATCH_TARGET = 501;
/** Лимит секунд на ход (игрок); совпадает с покер-практикой — неоновое кольцо на сиденье. */
const BELOT_HERO_TURN_SECONDS = 30;
/** Задержка хода бота (мс); кольцо на сиденье бота привязано к этому окну, как у оппонентов в покере. */
const BELOT_BOT_TURN_DELAY_MS = 700;
const LOCAL_STAKE_MIN = 100;
const LOCAL_STAKE_MAX = 10000;
const LOCAL_STAKE_STEP = 100;
const LOCAL_RAKE_PERCENT = 2;
const GRAND_VICTORY_SPARKS = [
  { l: 8, t: 16, d: 0, c: '#fbbf24' },
  { l: 18, t: 10, d: 0.12, c: '#f472b6' },
  { l: 86, t: 20, d: 0.26, c: '#22d3ee' },
  { l: 74, t: 12, d: 0.08, c: '#a78bfa' },
  { l: 12, t: 56, d: 0.34, c: '#fde047' },
  { l: 44, t: 8, d: 0.2, c: '#fb7185' },
  { l: 56, t: 66, d: 0.5, c: '#34d399' },
  { l: 90, t: 44, d: 0.18, c: '#fbbf24' },
];

function normalizeBelotTotals(totals, fallbackStake) {
  const safeStake = Math.max(LOCAL_STAKE_MIN, Math.min(LOCAL_STAKE_MAX, Math.trunc(Number(fallbackStake) || LOCAL_STAKE_MIN)));
  const fallbackTeamStake = safeStake * 2;
  const fallbackTotalPot = fallbackTeamStake * 2;
  const fallbackRakeAmount = Math.floor(fallbackTotalPot * (LOCAL_RAKE_PERCENT / 100));
  const fallbackPayoutPool = fallbackTotalPot - fallbackRakeAmount;
  const fallbackWinnerShare = Math.floor(fallbackPayoutPool / 2);
  const src = totals && typeof totals === 'object' ? totals : {};
  const rakePercent = Number.isFinite(Number(src.rakePercent)) ? Math.max(0, Math.floor(Number(src.rakePercent))) : LOCAL_RAKE_PERCENT;
  return {
    playerStake: Number.isFinite(Number(src.playerStake)) ? Math.max(0, Math.floor(Number(src.playerStake))) : safeStake,
    teamStake: Number.isFinite(Number(src.teamStake)) ? Math.max(0, Math.floor(Number(src.teamStake))) : fallbackTeamStake,
    totalPot: Number.isFinite(Number(src.totalPot)) ? Math.max(0, Math.floor(Number(src.totalPot))) : fallbackTotalPot,
    rakePercent,
    rakeAmount: Number.isFinite(Number(src.rakeAmount)) ? Math.max(0, Math.floor(Number(src.rakeAmount))) : fallbackRakeAmount,
    payoutPool: Number.isFinite(Number(src.payoutPool)) ? Math.max(0, Math.floor(Number(src.payoutPool))) : fallbackPayoutPool,
    winnerShare: Number.isFinite(Number(src.winnerShare)) ? Math.max(0, Math.floor(Number(src.winnerShare))) : fallbackWinnerShare,
  };
}

function teamLabelBySeat(seatIndex) {
  return seatIndex === 0 || seatIndex === 2 ? 'Команда A' : 'Команда B';
}

function toPlayingSuit(suit) {
  if (suit === 'H') return 'hearts';
  if (suit === 'D') return 'diamonds';
  if (suit === 'C') return 'clubs';
  return 'spades';
}

function getRelativeSeatPosition(seatIndex, heroIndex) {
  if (seatIndex < 0 || heroIndex < 0) return 'top';
  const offset = (seatIndex - heroIndex + 4) % 4;
  if (offset === 0) return 'bottom';
  if (offset === 1) return 'left';
  if (offset === 2) return 'top';
  return 'right';
}

const TRICK_SLOT_TRANSFORM = {
  top: 'translate(-50%, -132%) rotate(2deg)',
  right: 'translate(44%, -50%) rotate(8deg)',
  bottom: 'translate(-50%, 30%) rotate(-2deg)',
  left: 'translate(-144%, -50%) rotate(-8deg)',
};

const TRICK_COLLECT_TRANSFORM = {
  top: 'translate(-50%, -210%) scale(0.62)',
  right: 'translate(165%, -50%) scale(0.62)',
  bottom: 'translate(-50%, 110%) scale(0.62)',
  left: 'translate(-258%, -50%) scale(0.62)',
};
const DEAL_HERO_BURST_MS = 105;
const DEAL_HERO_STAGGER_MS = 22;
const DEAL_BETWEEN_SEATS_MS = 85;
const DEAL_SEAT_ORDER = [0, 1, 2, 3];
const DEAL_CARD_SOUND_VOL = 0.78;
const CLASSIC_SEAT_STORAGE_BLOCK = 'belot-classic';
/** Stable fallback for `teamCardScores` when round has no scores yet (avoid new array each render). */
const DEFAULT_TEAM_CARD_SCORES = Object.freeze([0, 0]);
const DEFAULT_CARDS_ON_TABLE = Object.freeze([]);

function classicPhaseLabelFromState(roundState) {
  if (!roundState) return 'Ожидание';
  if (roundState.status === 'in_progress') return 'Розыгрыш взятки';
  if (roundState.status === 'over') return 'Раунд завершён';
  if (roundState.status === 'started_selecting_suit') return 'Торги';
  if (roundState.status === 'suit_selected') return 'Контракт выбран';
  return String(roundState.status || 'Ожидание');
}

/** Буква козыря классического движка (C/D/H/S/N/A) → контракт для `BelotDeckTrumpIndicator`. */
function classicSuitLetterToDeckTrumpContract(suitLetter) {
  if (!suitLetter || SUIT_LABEL[suitLetter] == null) return null;
  if (suitLetter === 'N') return { trumpSuit: null, contractTargetPoints: 1 };
  if (suitLetter === 'A') return { trumpSuit: 'all_trump', contractTargetPoints: 1 };
  const byLetter = { C: 0, D: 1, H: 2, S: 3 };
  const idx = byLetter[suitLetter];
  if (idx == null) return null;
  return { trumpSuit: idx, contractTargetPoints: 1 };
}

function deckTrumpPhaseFromClassicRound(gameStarted, roundState) {
  if (!gameStarted) return 'init';
  if (roundState?.suit) return 'play';
  if (roundState?.status === 'started_selecting_suit') return 'auction';
  return 'init';
}

function seatTurnMetaLabel({ handSize, roundDealAnimActive }) {
  if (roundDealAnimActive) return 'Раздача';
  return `${handSize} карт`;
}

function SeatLastMoveBadge({ action }) {
  if (!action?.kind) return null;
  const tone =
    action.kind === 'trick'
      ? 'border-emerald-500/40 bg-emerald-950/20 text-emerald-50/95'
      : 'border-cyan-500/35 bg-cyan-950/20 text-cyan-50/95';
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-md border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
        tone,
      )}
      title={action.label}
    >
      {action.label}
    </span>
  );
}

function flyHeroDealCardFromDeck(deckEl, targetEl, onFlightStartSound, cardSize = 'md') {
  return new Promise((resolve) => {
    const deckRect = deckEl.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    if (deckRect.width < 2 || targetRect.width < 2) {
      resolve();
      return;
    }
    onFlightStartSound();
    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = `${deckRect.left}px`;
    wrapper.style.top = `${deckRect.top}px`;
    wrapper.style.width = `${deckRect.width}px`;
    wrapper.style.height = `${deckRect.height}px`;
    wrapper.style.zIndex = '120';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.transformOrigin = 'top left';
    document.body.appendChild(wrapper);
    const root = createRoot(wrapper);
    root.render(<PlayingCard faceDown size={cardSize} />);
    requestAnimationFrame(() => {
      try {
        const tr = targetEl.getBoundingClientRect();
        const wr = wrapper.getBoundingClientRect();
        if (wr.width < 1) {
          try { root.unmount(); } catch { /* ignore */ }
          try { wrapper.remove(); } catch { /* ignore */ }
          resolve();
          return;
        }
        const tx = tr.left - wr.left;
        const ty = tr.top - wr.top;
        const sx = tr.width / Math.max(wr.width, 1);
        const sy = tr.height / Math.max(wr.height, 1);
        const anim = wrapper.animate(
          [
            { transform: 'translate(0px, 0px) scale(1, 1)' },
            { transform: `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})` },
          ],
          {
            duration: DEAL_HERO_BURST_MS,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            fill: 'forwards',
          },
        );
        anim.finished
          .catch(() => {})
          .finally(() => {
            try { root.unmount(); } catch { /* ignore */ }
            try { wrapper.remove(); } catch { /* ignore */ }
            resolve();
          });
      } catch {
        try { root.unmount(); } catch { /* ignore */ }
        try { wrapper.remove(); } catch { /* ignore */ }
        resolve();
      }
    });
  });
}

function autoBid(round) {
  let guard = 0;
  while (round.status === 'started_selecting_suit' && guard < 24) {
    guard += 1;
    const p = round.players[round.playerTurn];
    const valid = round.getValidSuitCalls(p);
    if (!valid.length) break;
    let call = 'P';
    let modifier = 1;
    const suitsOnly = valid.filter((v) => ['C', 'D', 'H', 'S', 'N', 'A'].includes(v));
    if (!round.suit && suitsOnly.length > 0 && Math.random() < 0.55) {
      call = suitsOnly[Math.floor(Math.random() * suitsOnly.length)];
    } else if (round.suit && suitsOnly.length > 0 && Math.random() < 0.25) {
      call = suitsOnly[suitsOnly.length - 1];
    } else if (valid.includes('x2') && Math.random() < 0.12) {
      call = round.suit;
      modifier = 2;
    } else if (valid.includes('x4') && Math.random() < 0.06) {
      call = round.suit;
      modifier = 4;
    }
    round.callSuit(p, call, modifier);
  }
  return round.status;
}

function startRoundFlow(game) {
  const splitBy = game.currentRound.players[game.roundNum % 4];
  const splitPos = Math.floor(Math.random() * 30) + 1;
  game.startRound(splitBy, splitPos);
  autoBid(game.currentRound);
  if (game.currentRound.status === 'suit_selected') {
    game.currentRound.initPlayStage();
  }
  if (game.currentRound.status === 'over' && !game.currentRound.suit) {
    game.finishRoundAndScore();
    return startRoundFlow(game);
  }
  return true;
}

function pickBotCard(round, botName) {
  const options = round.getPlayerOptions(botName);
  if (!options.length) return null;
  return options[Math.floor(Math.random() * options.length)];
}

function BelotLobbyInner({ user, onBack, onUserPatch }) {
  const { play } = useAudio();
  const heroName = useMemo(() => {
    const n = (user?.username || 'Игрок').trim();
    return n || 'Игрок';
  }, [user?.username]);
  const prevPlayTurnNameRef = useRef(null);
  const [, setBotCount] = useState(ALLOWED_BOT_COUNTS[0]);
  const [selectedStake, setSelectedStake] = useState(LOCAL_STAKE_MIN);
  const [rulesVariant, setRulesVariant] = useState('classic');
  const [armenianTargetScore, setArmenianTargetScore] = useState(151);
  const [moldovanTargetScore, setMoldovanTargetScore] = useState(501);
  const [armenianStake, setArmenianStake] = useState(LOCAL_STAKE_MIN);
  const [armenianBotPlayMode, setArmenianBotPlayMode] = useState('simple');
  const [openArmenianLobby, setOpenArmenianLobby] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [game, setGame] = useState(null);
  const gameRef = useRef(null);
  const [tick, setTick] = useState(0);
  const [heroTurnSecondsLeft, setHeroTurnSecondsLeft] = useState(null);
  const [oppTurnClock, setOppTurnClock] = useState(null);
  const [oppRingTick, setOppRingTick] = useState(0);
  const [belotStatsOpen, setBelotStatsOpen] = useState(false);
  const [message, setMessage] = useState('Запустите матч Белота против ботов.');
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const [roundResultFx, setRoundResultFx] = useState(null);
  const [matchResultFx, setMatchResultFx] = useState(null);
  const [trickView, setTrickView] = useState({
    cards: [],
    winnerName: null,
    winnerCardKey: null,
    winnerPosition: 'top',
    phase: 'idle',
    token: 0,
  });
  const trickTimersRef = useRef([]);
  const animatedTrickRef = useRef(0);
  const shownRoundFxRef = useRef(-1);
  const [belotCopyFeedback, setBelotCopyFeedback] = useState(false);
  const belotCopyTimerRef = useRef(null);
  const matchStakeRef = useRef(LOCAL_STAKE_MIN);
  const matchTotalsRef = useRef(null);
  const matchSettledRef = useRef(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [economyBusy, setEconomyBusy] = useState(false);
  const [sessionRecoveryBusy, setSessionRecoveryBusy] = useState(false);
  const [remoteActiveSession, setRemoteActiveSession] = useState(null);
  const [isSettling, setIsSettling] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const [entryDebitToast, setEntryDebitToast] = useState('');
  const [roundDealAnimActive, setRoundDealAnimActive] = useState(false);
  const [seatDealSlotRevealed, setSeatDealSlotRevealed] = useState(null);
  const [dealVisualEpoch, setDealVisualEpoch] = useState(0);
  const [, setDealFinishedNonce] = useState(0);
  const matchSessionIdRef = useRef(null);
  const handSlotRefs = useMemo(() => Array.from({ length: 8 }, () => React.createRef()), []);
  const seatCardSlotRefs = useMemo(
    () => Array.from({ length: 4 }, () => Array.from({ length: 8 }, () => React.createRef())),
    [],
  );
  const deckRef = useRef(null);
  const dealAnimGenRef = useRef(0);
  const dealFlightAbortRef = useRef(false);
  const lastBumpedDealGenRef = useRef(-1);
  const trickCenterRef = useRef(null);
  const seatArenaRef = useRef(null);
  const suppressNextCardClickRef = useRef(false);
  const suppressClickTimeoutRef = useRef(null);
  const dragPlayRef = useRef(null);
  const dragMoveRafRef = useRef(null);
  const dragPointerRef = useRef({ x: 0, y: 0 });
  const dragDropActiveRef = useRef(false);
  const dragCancelTimeoutRef = useRef(null);
  const dragSnapTimeoutRef = useRef(null);
  const [dragPlayCard, setDragPlayCard] = useState(null);
  const [lastSeatActions, setLastSeatActions] = useState(() => ({}));
  const {
    seatArrangeMode,
    setSeatArrangeMode,
    seatOffsetBySeat,
    effectiveLayoutPreset,
    handleSeatDragPointerDown,
    handleSeatDragPointerMove,
    handleSeatDragPointerUp,
    handleSeatDragLostCapture,
    handleClearSeatOffsets,
  } = useBelotSeatLayout({
    sessionStarted: gameStarted,
    seatArenaRef,
    storageScope: SEAT_OFFSET_STORAGE.local,
    storageBlock: CLASSIC_SEAT_STORAGE_BLOCK,
  });
  const hasJwtToken = useMemo(() => {
    try {
      return Boolean(localStorage.getItem('token'));
    } catch {
      return false;
    }
  }, []);
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }, []);

  // Classic engine mutates `game` in place; `tick` bumps after mutations so derived memos refresh.
  const roundState = useMemo(() => game?.currentRound?.getRoundStatus() ?? null, [game, tick]); // eslint-disable-line react-hooks/exhaustive-deps -- tick: in-place engine refresh
  const heroIndex = useMemo(() => {
    if (!game?.currentRound?.players) return 0;
    const i = game.currentRound.players.indexOf(heroName);
    return i >= 0 ? i : 0;
  }, [game, heroName, tick]); // eslint-disable-line react-hooks/exhaustive-deps -- tick: in-place engine refresh
  const myTurn = Boolean(game && roundState?.status === 'in_progress' && roundState?.pTurnName === heroName);
  const myOptions = useMemo(() => {
    if (!(game && myTurn)) return [];
    return game.currentRound.getPlayerOptions(heroName);
  }, [game, heroName, myTurn, tick]); // eslint-disable-line react-hooks/exhaustive-deps -- tick: in-place engine refresh
  const heroHand = useMemo(() => {
    if (!game?.currentRound?.hands?.[heroIndex]) return [];
    return [...game.currentRound.hands[heroIndex].cards];
  }, [game, heroIndex, tick]); // eslint-disable-line react-hooks/exhaustive-deps -- tick: in-place engine refresh
  const playableSet = useMemo(() => {
    const set = new Set();
    for (const c of myOptions) set.add(`${c.rank}${c.suit}`);
    return set;
  }, [myOptions]);
  const cardsOnTable = useMemo(() => {
    const c = roundState?.cardsOnTable;
    return Array.isArray(c) ? c : DEFAULT_CARDS_ON_TABLE;
  }, [roundState]);
  const trickCount = roundState?.trickCount ?? 0;
  const lastTrick = roundState?.lastTrick ?? null;
  const roundOver = roundState?.status === 'over';
  const teamTricks = roundState?.tricks ?? [0, 0];
  const teamCardScores = useMemo(() => {
    const cs = roundState?.cardScores;
    return Array.isArray(cs) && cs.length >= 2 ? cs : DEFAULT_TEAM_CARD_SCORES;
  }, [roundState]);
  const heroTeam = heroIndex === 0 || heroIndex === 2 ? 0 : 1;
  const oppTeam = heroTeam === 0 ? 1 : 0;
  const seatDensity = getSeatDensityPreset(TOTAL_PLAYERS);
  const seatArena = useMemo(() => getSeatArenaTuning(TOTAL_PLAYERS), []);
  const seatSlots = useMemo(() => {
    if (effectiveLayoutPreset === 'mobile-stack') return buildBelotFourMobileStackSlots();
    return buildEllipticSeatSlots({
      seats: SEAT_LAYOUT_ORDER,
      heroSeat: heroIndex,
      centerY: seatArena.centerY,
      radiusX: seatArena.radiusX,
      radiusY: seatArena.radiusY,
    });
  }, [effectiveLayoutPreset, heroIndex, seatArena.centerY, seatArena.radiusX, seatArena.radiusY]);
  const heroTotalScore = game?.teamScores?.[heroTeam] ?? 0;
  const oppTotalScore = game?.teamScores?.[oppTeam] ?? 0;
  const handCount = heroHand.length;
  const baseFanStep = handCount > 7 ? 34 : 40;
  const desiredFanWidth = Math.max(240, (handCount - 1) * baseFanStep + 84);
  const maxFanWidth = 340;
  const fanWidth = Math.min(desiredFanWidth, maxFanWidth);
  const fanStep = handCount > 1 ? (fanWidth - 84) / (handCount - 1) : 0;
  const fanBaseYOffset = -50;
  const classicPhaseLabel = useMemo(() => classicPhaseLabelFromState(roundState), [roundState]);
  const balanceValue = Math.max(0, Math.trunc(Number(user?.balance) || 0));
  const activeStake = rulesVariant === 'armenian' ? armenianStake : selectedStake;
  const hasEnoughBalance = balanceValue >= activeStake;
  const currentMatchStake = Math.max(LOCAL_STAKE_MIN, Math.min(LOCAL_STAKE_MAX, Math.trunc(Number(matchStakeRef.current) || LOCAL_STAKE_MIN)));
  const currentTotals = normalizeBelotTotals(matchTotalsRef.current, currentMatchStake);
  const currentPlayerStake = currentTotals.playerStake;
  const currentTotalPot = currentTotals.totalPot;
  const currentRakePercent = currentTotals.rakePercent;
  const currentRakeAmount = currentTotals.rakeAmount;
  const currentPayoutPool = currentTotals.payoutPool;
  const currentWinnerShare = currentTotals.winnerShare;
  const classicViewModel = useMemo(() => {
    const currentTurnSeat = roundState?.pTurn ?? -1;
    const trickRows = trickView.cards.map((card) => {
      const key = `${card.rank}${card.suit}-${card.placedBy}`;
      const seat = roundState?.players?.indexOf(card.placedBy) ?? -1;
      return {
        key,
        seat,
        rank: card.rank,
        suit: toPlayingSuit(card.suit),
        label: card.placedBy,
      };
    });
    const heroCards = heroHand.map((card, idx) => {
      const playableKey = `${card.rank}${card.suit}`;
      const canPlay = myTurn && playableSet.has(playableKey);
      return { key: `${playableKey}-${idx}`, card, idx, canPlay, cardId: playableKey };
    });
    return {
      currentTurnSeat,
      trickRows,
      heroCards,
      contractLabel: roundState?.suit ? SUIT_LABEL[roundState.suit] : 'не выбран',
      tablePot: Math.max(0, teamCardScores[0] + teamCardScores[1]),
    };
  }, [heroHand, myTurn, playableSet, roundState, teamCardScores, trickView.cards]);

  const deckTrumpContractClassic = useMemo(
    () => classicSuitLetterToDeckTrumpContract(roundState?.suit),
    [roundState?.suit],
  );
  const deckTrumpPhaseClassic = useMemo(
    () => deckTrumpPhaseFromClassicRound(gameStarted, roundState),
    [gameStarted, roundState],
  );
  const prefersReducedMotionDeckTrumpClassic = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }, []);

  const seatBotCards = useMemo(() => {
    const handSizes = roundState?.handSizes || [];
    return Array.from({ length: 4 }, (_, seat) => {
      if (seat === heroIndex) return [];
      const count = Math.max(0, Math.min(8, Number(handSizes[seat]) || 0));
      return Array.from({ length: count }, () => ({ rank: 'A', suit: 'spades' }));
    });
  }, [heroIndex, roundState?.handSizes]);
  const classicDealState = useMemo(() => {
    const rawHands = Array.isArray(game?.currentRound?.hands)
      ? game.currentRound.hands.map((h) => (Array.isArray(h?.cards) ? h.cards : []))
      : [[], [], [], []];
    return {
      phase: gameStarted ? 'auction' : 'idle',
      roundIndex: Math.max(0, Number(game?.roundNum || 1) - 1),
      hands: rawHands,
    };
  }, [game, gameStarted, tick]); // eslint-disable-line react-hooks/exhaustive-deps -- tick: in-place engine refresh

  useEffect(() => {
    gameRef.current = game;
  }, [game]);

  const autoPlayHeroOnDeadline = useCallback(() => {
    const g = gameRef.current;
    if (!g?.currentRound) return;
    const r = g.currentRound;
    if (r.status !== 'in_progress') return;
    if (r.players[r.playerTurn] !== heroName) return;
    const picked = pickBotCard(r, heroName);
    if (!picked) return;
    const ok = r.placeCard(heroName, picked.suit, picked.rank);
    if (!ok) return;
    play('card-flip', 0.75);
    if (r.status === 'over') {
      setMessage(
        `Раунд завершён. Счёт команд: ${belotMatchCpFromEnginePoints(g.teamScores[0])} : ${belotMatchCpFromEnginePoints(g.teamScores[1])} CP`,
      );
    }
    setTick((x) => x + 1);
  }, [heroName, play]);

  const clearTrickTimers = useCallback(() => {
    trickTimersRef.current.forEach((timer) => clearTimeout(timer));
    trickTimersRef.current = [];
  }, []);

  const markSeatAction = useCallback((seatIndex, kind, label) => {
    if (!Number.isInteger(seatIndex) || seatIndex < 0 || seatIndex > 3) return;
    setLastSeatActions((prev) => ({
      ...prev,
      [seatIndex]: {
        kind,
        label,
        at: Date.now(),
      },
    }));
  }, []);

  const resetTrickAnimationState = useCallback(() => {
    clearTrickTimers();
    animatedTrickRef.current = 0;
    setTrickView({
      cards: [],
      winnerName: null,
      winnerCardKey: null,
      winnerPosition: 'top',
      phase: 'idle',
      token: 0,
    });
  }, [clearTrickTimers]);

  const resetClassicToMenu = useCallback(() => {
    setGameStarted(false);
    setGame(null);
    setTick(0);
    setBelotStatsOpen(false);
    setRoundResultFx(null);
    setMatchResultFx(null);
    shownRoundFxRef.current = -1;
    matchSettledRef.current = false;
    matchSessionIdRef.current = null;
    matchTotalsRef.current = null;
    setIsSettling(false);
    setLeaveError('');
    dealFlightAbortRef.current = true;
    setSeatDealSlotRevealed(null);
    setRoundDealAnimActive(false);
    setMessage('Запустите матч Белота против ботов.');
  }, []);

  const callBelotEconomyApi = useCallback(async (path, body) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('AUTH_REQUIRED');
    const res = await fetch(`${getApiBaseUrl()}${path}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const code = String(data?.code || data?.error || `HTTP_${res.status}`);
      const err = new Error(code);
      // @ts-ignore
      err.payload = data;
      throw err;
    }
    return data;
  }, []);

  const loadRemoteActiveSession = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setRemoteActiveSession(null);
      return null;
    }
    const res = await fetch(`${getApiBaseUrl()}/api/belot/local/session/active`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await res.json().catch(() => ({}));
    const active = data?.activeSession || null;
    setRemoteActiveSession(active);
    const payloadBalance = Number(data?.balance);
    if (Number.isFinite(payloadBalance)) {
      onUserPatch?.({ balance: Math.max(0, Math.trunc(payloadBalance)) });
    }
    return active;
  }, [onUserPatch]);

  useBelotDealAnimation({
    engineState: classicDealState,
    sessionStarted: gameStarted,
    humanSeat: heroIndex,
    handSlotRefs,
    seatCardSlotRefs,
    deckRef,
    play,
    dealCardSoundVol: DEAL_CARD_SOUND_VOL,
    dealSeatOrder: DEAL_SEAT_ORDER,
    dealHeroStaggerMs: DEAL_HERO_STAGGER_MS,
    dealBetweenSeatsMs: DEAL_BETWEEN_SEATS_MS,
    dealAnimGenRef,
    dealFlightAbortRef,
    lastBumpedDealGenRef,
    setRoundDealAnimActive,
    setSeatDealSlotRevealed,
    setDealVisualEpoch,
    setDealFinishedNonce,
    flyHeroDealCardFromDeck,
  });

  const startMatch = useCallback(() => {
    // Legacy classic runtime disabled: classic now always runs through moldovan variant
    // in BelotArmenianLocalLobby.
    setOpenArmenianLobby(true);
  }, []);

  const playMyCard = useCallback(
    (card) => {
      if (!game || !myTurn || !card) return;
      const ok = game.currentRound.placeCard(heroName, card.suit, card.rank);
      if (!ok) return;
      markSeatAction(heroIndex, 'play', 'Ход');
      play('card-flip');
      if (game.currentRound.status === 'over') {
        setMessage(
          `Раунд завершён. Счёт команд: ${belotMatchCpFromEnginePoints(game.teamScores[0])} : ${belotMatchCpFromEnginePoints(game.teamScores[1])} CP`,
        );
      }
      setTick((x) => x + 1);
    },
    [game, heroIndex, heroName, markSeatAction, myTurn, play],
  );

  const playMyCardById = useCallback(
    (cardId) => {
      if (!cardId || !Array.isArray(heroHand)) return;
      const picked = heroHand.find((c) => `${c.rank}${c.suit}` === cardId);
      if (!picked) return;
      playMyCard(picked);
    },
    [heroHand, playMyCard],
  );

  const getPointerDropMetric = useCallback(
    (x, y) => getTrickCenterDropMetric(trickCenterRef.current, x, y),
    [],
  );

  const clearDragPlayCard = useCallback(() => {
    if (dragMoveRafRef.current != null) {
      window.cancelAnimationFrame(dragMoveRafRef.current);
      dragMoveRafRef.current = null;
    }
    if (dragCancelTimeoutRef.current != null) {
      window.clearTimeout(dragCancelTimeoutRef.current);
      dragCancelTimeoutRef.current = null;
    }
    if (dragSnapTimeoutRef.current != null) {
      window.clearTimeout(dragSnapTimeoutRef.current);
      dragSnapTimeoutRef.current = null;
    }
    dragDropActiveRef.current = false;
    dragPlayRef.current = null;
    setDragPlayCard(null);
  }, []);

  const suppressNextCardClick = useCallback((ms = 320) => {
    suppressNextCardClickRef.current = true;
    if (suppressClickTimeoutRef.current != null) {
      window.clearTimeout(suppressClickTimeoutRef.current);
    }
    suppressClickTimeoutRef.current = window.setTimeout(() => {
      suppressClickTimeoutRef.current = null;
      suppressNextCardClickRef.current = false;
    }, ms);
  }, []);

  const onHeroCardPointerDown = useCallback((event, card, idx, canPlay) => {
    if (!canPlay || roundDealAnimActive || !myTurn) return;
    event.preventDefault();
    event.stopPropagation();
    const cardRect = event.currentTarget?.getBoundingClientRect?.();
    const originX = cardRect ? cardRect.left + cardRect.width / 2 : event.clientX;
    const originY = cardRect ? cardRect.top + cardRect.height / 2 : event.clientY;
    const cardId = `${card.rank}${card.suit}`;
    dragDropActiveRef.current = false;
    dragPlayRef.current = {
      pointerId: event.pointerId,
      cardId,
      originIndex: idx,
      originX,
      originY,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
    setDragPlayCard({
      cardId,
      originIndex: idx,
      x: event.clientX,
      y: event.clientY,
      originX,
      originY,
      rank: card.rank,
      suit: toPlayingSuit(card.suit),
      dropActive: false,
      returning: false,
    });
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
  }, [myTurn, roundDealAnimActive]);

  const onHeroCardPointerMove = useCallback((event) => {
    const d = dragPlayRef.current;
    if (!d || event.pointerId !== d.pointerId) return;
    const dx = event.clientX - d.startX;
    const dy = event.clientY - d.startY;
    const moved = Math.hypot(dx, dy) >= 6;
    if (moved && !d.moved) d.moved = true;
    dragPointerRef.current = { x: event.clientX, y: event.clientY };
    if (dragMoveRafRef.current != null) return;
    dragMoveRafRef.current = window.requestAnimationFrame(() => {
      dragMoveRafRef.current = null;
      const current = dragPointerRef.current;
      const metric = getPointerDropMetric(current.x, current.y);
      const nextDropActive =
        d.moved &&
        (dragDropActiveRef.current ? metric <= 1.16 : metric <= 1.0);
      dragDropActiveRef.current = nextDropActive;
      setDragPlayCard((prev) => (
        prev
          ? {
            ...prev,
            x: current.x,
            y: current.y,
            dropActive: nextDropActive,
            returning: false,
          }
          : prev
      ));
    });
  }, [getPointerDropMetric]);

  const onHeroCardPointerUp = useCallback((event) => {
    const d = dragPlayRef.current;
    if (!d || event.pointerId !== d.pointerId) return;
    const metric = getPointerDropMetric(event.clientX, event.clientY);
    const dropActive = dragDropActiveRef.current ? metric <= 1.16 : metric <= 1.0;
    const moved = Boolean(d.moved);
    const cardId = d.cardId;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
    if (moved && !dropActive) {
      const originX = d.originX ?? event.clientX;
      const originY = d.originY ?? event.clientY;
      suppressNextCardClick();
      setDragPlayCard((prev) => (
        prev
          ? { ...prev, x: originX, y: originY, returning: true, dropActive: false }
          : prev
      ));
      dragSnapTimeoutRef.current = window.setTimeout(() => {
        dragSnapTimeoutRef.current = null;
        clearDragPlayCard();
      }, 180);
      return;
    }
    clearDragPlayCard();
    if (!moved) {
      suppressNextCardClick(220);
      playMyCardById(cardId);
      return;
    }
    suppressNextCardClick();
    if (dropActive) playMyCardById(cardId);
  }, [clearDragPlayCard, getPointerDropMetric, playMyCardById, suppressNextCardClick]);

  const onHeroCardPointerCancel = useCallback(() => {
    const d = dragPlayRef.current;
    if (!d) {
      clearDragPlayCard();
      return;
    }
    if (dragCancelTimeoutRef.current != null) {
      window.clearTimeout(dragCancelTimeoutRef.current);
    }
    dragCancelTimeoutRef.current = window.setTimeout(() => {
      dragCancelTimeoutRef.current = null;
      clearDragPlayCard();
    }, 90);
  }, [clearDragPlayCard]);

  const onHeroCardClick = useCallback((card) => {
    if (suppressNextCardClickRef.current) {
      suppressNextCardClickRef.current = false;
      if (suppressClickTimeoutRef.current != null) {
        window.clearTimeout(suppressClickTimeoutRef.current);
        suppressClickTimeoutRef.current = null;
      }
      return;
    }
    playMyCard(card);
  }, [playMyCard]);

  const startNextRound = () => {
    if (!game) return;
    play('button-click');
    if (game.currentRound.status === 'over') {
      game.finishRoundAndScore();
      const heroScore = game.teamScores[heroTeam] ?? 0;
      const enemyScore = game.teamScores[oppTeam] ?? 0;
      const maxScore = Math.max(heroScore, enemyScore);
      if (maxScore >= BELOT_MATCH_TARGET && heroScore !== enemyScore) {
        setRoundResultFx(null);
        setMatchResultFx(heroScore > enemyScore ? 'grand-win' : 'grand-lose');
        setTick((x) => x + 1);
        return;
      }
    }
    startRoundFlow(game);
    resetTrickAnimationState();
    setRoundResultFx(null);
    setTick((x) => x + 1);
    setMessage(`Новый раунд #${game.roundNum + 1}`);
  };

  useEffect(() => {
    if (!game || !roundState) {
      setOppTurnClock(null);
      return undefined;
    }
    if (roundDealAnimActive) {
      setOppTurnClock(null);
      return undefined;
    }
    if (roundState.status !== 'in_progress') {
      setOppTurnClock(null);
      return undefined;
    }
    if (roundState.pTurnName === heroName) {
      setOppTurnClock(null);
      return undefined;
    }
    setOppTurnClock({ endAt: Date.now() + BELOT_BOT_TURN_DELAY_MS, totalMs: BELOT_BOT_TURN_DELAY_MS });
    const iv = setInterval(() => setOppRingTick((x) => x + 1), 50);
    return () => {
      clearInterval(iv);
      setOppTurnClock(null);
    };
  }, [game, heroName, roundDealAnimActive, roundState]);

  useEffect(() => {
    if (!game || !roundState) return undefined;
    if (roundDealAnimActive) return undefined;
    if (roundState.status !== 'in_progress') return undefined;
    if (roundState.pTurnName === heroName) return undefined;

    const botName = roundState.pTurnName;
    const id = setTimeout(() => {
      const picked = pickBotCard(game.currentRound, botName);
      if (picked) {
        game.currentRound.placeCard(botName, picked.suit, picked.rank);
        const botSeat = game.currentRound.players.indexOf(botName);
        markSeatAction(botSeat, 'play', 'Ход');
        play('card-deal', 0.48);
        if (game.currentRound.status === 'over') {
          setMessage(
          `Раунд завершён. Счёт команд: ${belotMatchCpFromEnginePoints(game.teamScores[0])} : ${belotMatchCpFromEnginePoints(game.teamScores[1])} CP`,
        );
        }
        setTick((x) => x + 1);
      }
    }, BELOT_BOT_TURN_DELAY_MS);
    return () => clearTimeout(id);
  }, [game, heroName, markSeatAction, play, roundDealAnimActive, roundState]);

  useEffect(() => {
    if (!game?.currentRound) {
      setHeroTurnSecondsLeft(null);
      return undefined;
    }
    if (roundDealAnimActive) {
      setHeroTurnSecondsLeft(null);
      return undefined;
    }
    if (!roundState || roundState.status !== 'in_progress' || roundState.pTurnName !== heroName) {
      setHeroTurnSecondsLeft(null);
      return undefined;
    }

    const deadline = Date.now() + BELOT_HERO_TURN_SECONDS * 1000;
    setHeroTurnSecondsLeft(BELOT_HERO_TURN_SECONDS);

    const intervalId = setInterval(() => {
      const left = Math.ceil((deadline - Date.now()) / 1000);
      setHeroTurnSecondsLeft(Math.max(0, left));
    }, 200);

    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      setHeroTurnSecondsLeft(null);
      autoPlayHeroOnDeadline();
    }, BELOT_HERO_TURN_SECONDS * 1000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      setHeroTurnSecondsLeft(null);
    };
  }, [game, heroName, roundDealAnimActive, roundState, autoPlayHeroOnDeadline]);

  useEffect(() => () => clearTrickTimers(), [clearTrickTimers]);
  useEffect(() => () => {
    if (suppressClickTimeoutRef.current != null) {
      window.clearTimeout(suppressClickTimeoutRef.current);
      suppressClickTimeoutRef.current = null;
    }
    if (dragMoveRafRef.current != null) {
      window.cancelAnimationFrame(dragMoveRafRef.current);
      dragMoveRafRef.current = null;
    }
    if (dragCancelTimeoutRef.current != null) {
      window.clearTimeout(dragCancelTimeoutRef.current);
      dragCancelTimeoutRef.current = null;
    }
    if (dragSnapTimeoutRef.current != null) {
      window.clearTimeout(dragSnapTimeoutRef.current);
      dragSnapTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!roundState?.players) return;

    if (trickCount < animatedTrickRef.current) {
      resetTrickAnimationState();
    }

    if (cardsOnTable.length > 0) {
      clearTrickTimers();
      setTrickView({
        cards: cardsOnTable.map((card) => ({ ...card })),
        winnerName: null,
        winnerCardKey: null,
        winnerPosition: 'top',
        phase: 'live',
        token: trickCount,
      });
      return;
    }

    if (lastTrick && trickCount > animatedTrickRef.current) {
      clearTrickTimers();
      const winnerName = lastTrick.strongest?.placedBy ?? null;
      const winnerSeat = roundState.players.indexOf(winnerName);
      const winnerPosition = getRelativeSeatPosition(winnerSeat, heroIndex);
      const winnerCardKey = lastTrick.strongest
        ? `${lastTrick.strongest.rank}${lastTrick.strongest.suit}-${lastTrick.strongest.placedBy}`
        : null;

      setTrickView({
        cards: lastTrick.cards.map((card) => ({ ...card })),
        winnerName,
        winnerCardKey,
        winnerPosition,
        phase: 'reveal',
        token: trickCount,
      });

      if (winnerName === heroName) play('win', 0.34);
      else play('chip-single', 0.5);
      markSeatAction(winnerSeat, 'trick', 'Взял');

      const revealTimer = setTimeout(() => {
        setTrickView((prev) => (prev.token === trickCount ? { ...prev, phase: 'collect' } : prev));
        play('chip-single', 0.35);
      }, 1000);
      const clearTimer = setTimeout(() => {
        setTrickView((prev) => (
          prev.token === trickCount
            ? { ...prev, cards: [], winnerName: null, winnerCardKey: null, phase: 'idle' }
            : prev
        ));
      }, 1700);
      trickTimersRef.current = [revealTimer, clearTimer];
      animatedTrickRef.current = trickCount;
    }
  }, [cardsOnTable, clearTrickTimers, heroIndex, heroName, lastTrick, markSeatAction, play, resetTrickAnimationState, roundState, trickCount]);

  useEffect(() => {
    if (!gameStarted || !roundState) return;
    if (roundState.status !== 'in_progress') {
      prevPlayTurnNameRef.current = null;
      return;
    }
    const name = roundState.pTurnName;
    if (name === heroName && prevPlayTurnNameRef.current !== heroName) {
      play('your-turn');
    }
    prevPlayTurnNameRef.current = name;
  }, [gameStarted, heroName, play, roundState]);

  useEffect(() => {
    if (gameStarted || !hasJwtToken) return;
    loadRemoteActiveSession().catch(() => {
      /* ignore active-session probe errors */
    });
  }, [gameStarted, hasJwtToken, loadRemoteActiveSession]);

  useEffect(() => {
    if (!roundResultFx || matchResultFx) return;
    if (roundResultFx === 'win') play('win', 0.92);
    else if (roundResultFx === 'lose') play('lose', 0.92);
    else play('check', 0.78);
  }, [roundResultFx, matchResultFx, play]);

  useEffect(() => {
    if (matchResultFx === 'grand-win') {
      play('win', 1);
      const t = setTimeout(() => play('showdown', 0.55), 220);
      return () => clearTimeout(t);
    }
    if (matchResultFx === 'grand-lose') play('lose', 1);
  }, [matchResultFx, play]);

  useEffect(() => {
    if (!gameStarted || matchSettledRef.current) return;
    if (matchResultFx !== 'grand-win' && matchResultFx !== 'grand-lose') return;
    if (!matchSessionIdRef.current) return;
    setIsSettling(true);
    (async () => {
      try {
        const heroScore = Number(game?.teamScores?.[heroTeam] ?? 0);
        const enemyScore = Number(game?.teamScores?.[oppTeam] ?? 0);
        const data = await callBelotEconomyApi('/api/belot/local/session/settle', {
          sessionId: matchSessionIdRef.current,
          usScore: heroScore,
          themScore: enemyScore,
        });
        if (typeof data?.balance === 'number' && Number.isFinite(data.balance)) {
          onUserPatch?.({ balance: Math.max(0, Math.trunc(data.balance)) });
        }
        matchTotalsRef.current = normalizeBelotTotals(data?.session?.totals, matchStakeRef.current);
        matchSettledRef.current = true;
      } catch {
        setMessage('Ошибка синхронизации результата матча с сервером.');
      } finally {
        setIsSettling(false);
      }
    })();
  }, [callBelotEconomyApi, game, gameStarted, heroTeam, matchResultFx, onUserPatch, oppTeam]);

  const handleLeaveRequest = useCallback(() => {
    if (!gameStarted) {
      onBack();
      return;
    }
    if (matchResultFx === 'grand-win' || matchResultFx === 'grand-lose' || matchSettledRef.current) {
      resetClassicToMenu();
      onBack();
      return;
    }
    if (isSettling) {
      setMessage('Подождите: идёт синхронизация итогов матча.');
      return;
    }
    setLeaveError('');
    setLeaveConfirmOpen(true);
  }, [gameStarted, isSettling, matchResultFx, onBack, resetClassicToMenu]);

  const confirmLeaveWithForfeit = useCallback(async () => {
    setLeaveConfirmOpen(false);
    setLeaveError('');
    if (!gameStarted) {
      onBack();
      return;
    }
    try {
      if (matchSessionIdRef.current && !matchSettledRef.current) {
        setEconomyBusy(true);
        const data = await callBelotEconomyApi('/api/belot/local/session/forfeit', {
          sessionId: matchSessionIdRef.current,
        });
        if (typeof data?.balance === 'number' && Number.isFinite(data.balance)) {
          onUserPatch?.({ balance: Math.max(0, Math.trunc(data.balance)) });
        }
        matchTotalsRef.current = normalizeBelotTotals(data?.session?.totals, matchStakeRef.current);
      }
      setEconomyBusy(false);
      resetClassicToMenu();
      onBack();
    } catch {
      setEconomyBusy(false);
      setLeaveError('Не удалось зафиксировать выход на сервере. Проверьте связь и повторите.');
      setLeaveConfirmOpen(true);
    }
  }, [callBelotEconomyApi, gameStarted, onBack, onUserPatch, resetClassicToMenu]);

  const recoverStuckServerSession = useCallback(async () => {
    if (sessionRecoveryBusy) return;
    setSessionRecoveryBusy(true);
    try {
      const payload = remoteActiveSession?.id ? { sessionId: remoteActiveSession.id } : {};
      const data = await callBelotEconomyApi('/api/belot/local/session/forfeit', payload);
      const payloadBalance = Number(data?.balance);
      if (Number.isFinite(payloadBalance)) {
        onUserPatch?.({ balance: Math.max(0, Math.trunc(payloadBalance)) });
      }
      setMessage('Серверная активная сессия завершена. Можно начать новый матч.');
      await loadRemoteActiveSession();
    } catch (e) {
      setMessage(`Не удалось завершить зависший матч: ${String(e?.message || 'unknown')}`);
    } finally {
      setSessionRecoveryBusy(false);
    }
  }, [callBelotEconomyApi, loadRemoteActiveSession, onUserPatch, remoteActiveSession?.id, sessionRecoveryBusy]);

  useEffect(() => {
    if (!game || !roundOver || matchResultFx) return;
    if (shownRoundFxRef.current === game.roundNum) return;
    shownRoundFxRef.current = game.roundNum;

    const heroRound = teamCardScores[heroTeam] ?? 0;
    const oppRound = teamCardScores[oppTeam] ?? 0;
    if (heroRound > oppRound) {
      setRoundResultFx('win');
    } else if (heroRound < oppRound) {
      setRoundResultFx('lose');
    } else {
      setRoundResultFx('draw');
    }
  }, [game, heroTeam, matchResultFx, oppTeam, roundOver, teamCardScores]);

  const flashBelotCopyOk = useCallback(() => {
    setBelotCopyFeedback(true);
    if (belotCopyTimerRef.current) clearTimeout(belotCopyTimerRef.current);
    belotCopyTimerRef.current = setTimeout(() => {
      setBelotCopyFeedback(false);
      belotCopyTimerRef.current = null;
    }, 2200);
  }, []);

  const copyBelotRoundSummary = useCallback(async () => {
    const lines = [
      '=== Белот — итог раунда ===',
      `Время: ${new Date().toLocaleString('ru-RU')}`,
      `Исход: ${roundResultFx === 'win' ? 'победа' : roundResultFx === 'lose' ? 'поражение' : 'ничья'}`,
      `Очки раунда: ${teamCardScores[heroTeam]} : ${teamCardScores[oppTeam]}`,
      `Счёт матча: ${heroTotalScore} : ${oppTotalScore} (цель ${BELOT_MATCH_TARGET})`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      flashBelotCopyOk();
    } catch {
      /* ignore */
    }
  }, [
    roundResultFx,
    teamCardScores,
    heroTeam,
    oppTeam,
    heroTotalScore,
    oppTotalScore,
    flashBelotCopyOk,
  ]);

  const copyBelotGrandWinSummary = useCallback(async () => {
    const lines = [
      '=== Белот — тотальная победа ===',
      `Время: ${new Date().toLocaleString('ru-RU')}`,
      `Итог матча: ${heroTotalScore} : ${oppTotalScore}`,
      `Банк: ${currentTotalPot} · Комиссия ${currentRakePercent}%: ${currentRakeAmount} · Выплата команде: ${currentPayoutPool}`,
      `Чистый выигрыш на игрока: ${currentWinnerShare} монет`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      flashBelotCopyOk();
    } catch {
      /* ignore */
    }
  }, [heroTotalScore, oppTotalScore, flashBelotCopyOk, currentTotalPot, currentRakePercent, currentRakeAmount, currentPayoutPool, currentWinnerShare]);

  const copyBelotGrandLoseSummary = useCallback(async () => {
    const lines = [
      '=== Белот — тотальное поражение ===',
      `Время: ${new Date().toLocaleString('ru-RU')}`,
      `Итог матча: ${heroTotalScore} : ${oppTotalScore}`,
      `Банк: ${currentTotalPot} · Комиссия ${currentRakePercent}%: ${currentRakeAmount} · Выплата победителям: ${currentPayoutPool}`,
      `Списано с игрока: ${currentPlayerStake} монет`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      flashBelotCopyOk();
    } catch {
      /* ignore */
    }
  }, [heroTotalScore, oppTotalScore, flashBelotCopyOk, currentTotalPot, currentRakePercent, currentRakeAmount, currentPayoutPool, currentPlayerStake]);

  const resetClassicDefaults = useCallback(() => {
    setBotCount(ALLOWED_BOT_COUNTS[0]);
    setSelectedStake(LOCAL_STAKE_MIN);
  }, []);

  const resetArmenianDefaults = useCallback(() => {
    setArmenianTargetScore(151);
    setArmenianStake(LOCAL_STAKE_MIN);
    setArmenianBotPlayMode('simple');
  }, []);

  const switchMode = useCallback((nextMode) => {
    if (nextMode !== 'classic' && nextMode !== 'armenian') return;
    setRulesVariant(nextMode);
    setOpenArmenianLobby(false);
    setMessage('Запустите матч Белота против ботов.');
    if (nextMode === 'classic') {
      resetClassicDefaults();
      return;
    }
    resetArmenianDefaults();
  }, [resetArmenianDefaults, resetClassicDefaults]);

  const startSelectedMode = useCallback(() => {
    if (rulesVariant === 'classic') {
      if (!hasEnoughBalance) {
        setMessage(
          `Недостаточно фишек для старта: нужно ${selectedStake.toLocaleString('ru-RU')}, доступно ${balanceValue.toLocaleString('ru-RU')}.`
        );
        play('notification');
        return;
      }
      if (!hasJwtToken) {
        setMessage('Для локального белота с экономикой нужна авторизация аккаунта.');
        play('notification');
        return;
      }
      setOpenArmenianLobby(true);
      return;
    }
    if (!hasEnoughBalance) {
      setMessage(
        `Недостаточно фишек для старта: нужно ${armenianStake.toLocaleString('ru-RU')}, доступно ${balanceValue.toLocaleString('ru-RU')}.`
      );
      play('notification');
      return;
    }
    if (!hasJwtToken) {
      setMessage('Для локального белота с экономикой нужна авторизация аккаунта.');
      play('notification');
      return;
    }
    setOpenArmenianLobby(true);
  }, [armenianStake, balanceValue, hasEnoughBalance, hasJwtToken, play, rulesVariant, selectedStake]);

  if (openArmenianLobby) {
    return (
      <BelotArmenianLocalLobby
        user={user}
        onBack={onBack}
        onUserPatch={onUserPatch}
        autoStart
        onSwitchClassic={() => {
          setRulesVariant('classic');
          setOpenArmenianLobby(false);
        }}
        modeVariant={rulesVariant === 'classic' ? 'moldovan' : 'armenian'}
        initialTargetScore={rulesVariant === 'classic' ? moldovanTargetScore : armenianTargetScore}
        initialStake={rulesVariant === 'classic' ? selectedStake : armenianStake}
        initialBotPlayMode={rulesVariant === 'classic' ? 'pro' : armenianBotPlayMode}
        initialDeclarationComboMode={rulesVariant === 'classic' ? 'casual' : 'hardcore'}
      />
    );
  }

  return (
    <div className="pt-root relative flex min-h-screen flex-col overflow-hidden bg-background text-foreground">
      {gameStarted && matchResultFx === 'grand-win' && (
        <div className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-black/40" aria-live="assertive">
          <div className="flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
            <div className="pointer-events-auto poker-fx-grand-victory-panel relative w-full max-w-[min(94vw,560px)] min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl border border-amber-400/50 bg-gradient-to-b from-amber-950/95 via-[#0f1428]/98 to-[#0a1520]/98 px-6 py-6 text-center shadow-[0_0_80px_rgba(251,191,36,0.35)] sm:px-8 sm:py-8">
            <div className="poker-fx-fireworks rounded-2xl" aria-hidden>
              {GRAND_VICTORY_SPARKS.map((s, i) => (
                <span
                  key={i}
                  className="poker-fx-firework-dot"
                  style={{
                    left: `${s.l}%`,
                    top: `${s.t}%`,
                    animationDelay: `${s.d}s`,
                    color: s.c,
                  }}
                />
              ))}
            </div>
            <div className="relative z-10">
              <Sparkles className="mx-auto mb-3 h-14 w-14 text-amber-300 drop-shadow-[0_0_18px_rgba(251,191,36,0.75)]" aria-hidden />
              <p className="text-xl font-black uppercase tracking-wide text-white drop-shadow-sm">
                Тотальная победа
              </p>
              <p className="mt-2 text-base font-bold text-amber-100/95">
                Выигран матч до {BELOT_MATCH_TARGET} очков.
              </p>
              <p className="mt-3 text-sm font-semibold text-amber-50/90">
                Итог: {heroTotalScore} : {oppTotalScore}
              </p>
              <p className="mt-1 text-xs text-amber-100/80">
                Банк: {currentTotalPot} · Комиссия {currentRakePercent}%: {currentRakeAmount} ·
                Выплата команде: {currentPayoutPool}
              </p>
              <p className="text-xs text-amber-100/80">Чистый выигрыш на игрока: {currentWinnerShare} монет</p>
              <button
                type="button"
                onClick={copyBelotGrandWinSummary}
                className="relative z-10 mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-amber-400/50 bg-amber-950/45 py-2.5 text-xs font-bold text-amber-100 transition hover:bg-amber-900/55"
              >
                <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {belotCopyFeedback ? 'Снимок скопирован' : 'Копировать итог матча'}
              </button>
              <div className="relative z-10 mt-6 flex w-full flex-col gap-2">
                <button
                  type="button"
                  onClick={startMatch}
                  className="flex w-full items-center justify-center rounded-xl border border-amber-400/55 bg-amber-950/55 py-3 text-sm font-bold text-amber-100 shadow-[0_0_24px_rgba(251,191,36,0.22)] transition hover:bg-amber-900/55"
                >
                  Начать матч заново
                </button>
                <button
                  type="button"
                  onClick={handleLeaveRequest}
                  className="flex w-full items-center justify-center rounded-xl border border-red-500/45 bg-red-950/50 py-3 text-sm font-bold text-red-200 transition hover:bg-red-900/55"
                >
                  Выйти из игры
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
      )}

      {gameStarted && matchResultFx === 'grand-lose' && (
        <div className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-black/30" aria-live="assertive">
          <div className="flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
            <div className="pointer-events-auto poker-fx-loss-panel w-full max-w-[min(94vw,560px)] min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl border border-red-500/45 bg-gradient-to-b from-red-950/90 to-[#0a1520]/95 px-6 py-6 text-center shadow-[0_0_40px_rgba(248,113,113,0.2)] sm:px-8 sm:py-7">
            <Skull className="mx-auto mb-2 h-11 w-11 text-red-300/90" aria-hidden />
            <p className="text-lg font-black tracking-wide text-white">Тотальное поражение</p>
            <p className="mt-1 text-sm text-red-100/80">Матч завершён не в вашу пользу.</p>
            <p className="mt-3 text-base font-bold leading-snug text-white">
              Итог: {heroTotalScore} : {oppTotalScore}
            </p>
            <p className="mt-1 text-xs text-red-100/80">
              Банк: {currentTotalPot} · Комиссия {currentRakePercent}%: {currentRakeAmount} ·
              Выплата победителям: {currentPayoutPool}
            </p>
            <p className="text-xs text-red-100/80">Списано с игрока: {currentPlayerStake} монет</p>
            <button
              type="button"
              onClick={copyBelotGrandLoseSummary}
              className="mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-red-400/45 bg-red-950/40 py-2.5 text-xs font-bold text-red-100 transition hover:bg-red-900/50"
            >
              <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {belotCopyFeedback ? 'Снимок скопирован' : 'Копировать итог матча'}
            </button>
            <div className="mt-6 flex w-full flex-col gap-2">
              <button
                type="button"
                onClick={startMatch}
                className="flex w-full items-center justify-center rounded-xl border border-amber-400/55 bg-amber-950/55 py-3 text-sm font-bold text-amber-100 shadow-[0_0_24px_rgba(251,191,36,0.18)] transition hover:bg-amber-900/55"
              >
                Начать матч заново
              </button>
              <button
                type="button"
                onClick={handleLeaveRequest}
                className="flex w-full items-center justify-center rounded-xl border border-red-500/45 bg-red-950/50 py-3 text-sm font-bold text-red-200 transition hover:bg-red-900/55"
              >
                Выйти из игры
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      {gameStarted && roundResultFx && !matchResultFx && (
        <div className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-black/25" aria-live="polite">
          <div className="flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
          <div
            className={cn(
              'pointer-events-auto w-full max-w-[min(94vw,560px)] min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl px-4 py-4 text-center sm:px-8 sm:py-7',
              roundResultFx === 'win' &&
                'poker-fx-win-panel border border-emerald-400/50 bg-gradient-to-b from-emerald-950/95 to-[#0a1520]/95 shadow-[0_0_60px_rgba(52,211,153,0.25)]',
              roundResultFx === 'lose' &&
                'poker-fx-loss-panel border border-red-500/45 bg-gradient-to-b from-red-950/90 to-[#0a1520]/95 shadow-[0_0_40px_rgba(248,113,113,0.2)]',
              roundResultFx === 'draw' &&
                'border border-amber-400/45 bg-gradient-to-b from-amber-950/90 to-[#0a1520]/95 shadow-lg'
            )}
          >
            {roundResultFx === 'win' && (
              <>
                <Trophy className="mx-auto mb-2 h-12 w-12 text-emerald-300 drop-shadow-[0_0_12px_rgba(52,211,153,0.6)]" aria-hidden />
                <p className="text-lg font-black tracking-wide text-white">Раунд выигран</p>
                <p className="mt-1 text-sm text-emerald-100/90">Отличная игра в этой взятке.</p>
              </>
            )}
            {roundResultFx === 'lose' && (
              <>
                <Skull className="mx-auto mb-2 h-11 w-11 text-red-300/90" aria-hidden />
                <p className="text-lg font-black tracking-wide text-white">Раунд проигран</p>
                <p className="mt-1 text-sm text-red-100/80">В следующем раунде можно отыграться.</p>
              </>
            )}
            {roundResultFx === 'draw' && (
              <>
                <p className="text-lg font-black tracking-wide text-amber-100">Ничья раунда</p>
                <p className="mt-1 text-sm text-amber-100/80">Команды набрали одинаково.</p>
              </>
            )}
            <p className="mt-3 text-base font-bold leading-snug text-white">
              Очки раунда: {teamCardScores[heroTeam]} : {teamCardScores[oppTeam]}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Матч: {heroTotalScore} : {oppTotalScore} (до {BELOT_MATCH_TARGET})
            </p>
            <button
              type="button"
              onClick={copyBelotRoundSummary}
              className={cn(
                'mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border py-2.5 text-xs font-bold transition',
                roundResultFx === 'win' &&
                  'border-emerald-400/45 bg-emerald-950/35 text-emerald-100 hover:bg-emerald-900/45',
                roundResultFx === 'lose' &&
                  'border-red-400/45 bg-red-950/35 text-red-100 hover:bg-red-900/45',
                roundResultFx === 'draw' &&
                  'border-amber-400/45 bg-amber-950/35 text-amber-100 hover:bg-amber-900/45'
              )}
            >
              <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {belotCopyFeedback ? 'Снимок скопирован' : 'Копировать итог раунда'}
            </button>
            <div className="mt-6 flex w-full flex-col gap-2">
              <button
                type="button"
                onClick={startNextRound}
                className="flex w-full items-center justify-center rounded-xl border border-emerald-500/40 bg-emerald-950/40 py-3 text-sm font-bold text-emerald-200 transition hover:bg-emerald-900/50"
              >
                Следующий раунд
              </button>
              <button
                type="button"
                onClick={handleLeaveRequest}
                className="flex w-full items-center justify-center rounded-xl border border-red-500/45 bg-red-950/50 py-3 text-sm font-bold text-red-200 transition hover:bg-red-900/55"
              >
                Выйти из игры
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      <ArcadeModeBackdrop mode="belot" />

      <GameIndustrialHeader
        floating
        tableName={`Классический белот · ${heroName} vs боты`}
        blindsLabel={gameStarted ? classicPhaseLabel : 'Локальный движок'}
        playersCount={TOTAL_PLAYERS}
        maxPlayers={TOTAL_PLAYERS}
        bucksBalance={user?.bucksBalance}
        balance={Number.isFinite(Number(user?.balance)) ? Math.max(0, Math.floor(Number(user.balance))) : undefined}
        onLeave={handleLeaveRequest}
        onSeatArrangeToggle={gameStarted ? () => setSeatArrangeMode((v) => !v) : undefined}
        seatArrangeMode={gameStarted && seatArrangeMode}
        onSeatLayoutReset={gameStarted ? handleClearSeatOffsets : undefined}
      />

      <main
        className={cn(
          gameStarted
            ? 'relative z-10 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 py-3'
            : 'relative z-10 mx-auto flex w-full max-w-xl flex-1 flex-col gap-3 overflow-y-auto px-3 py-4 pb-24'
        )}
      >
        {!gameStarted ? (
          <>
            <div className="relative overflow-hidden rounded-2xl border border-violet-300/20 bg-gradient-to-br from-[#2a1647]/88 via-[#21113a]/84 to-[#170c2b]/88 px-3 py-2 shadow-[0_12px_28px_rgba(0,0,0,0.34)]">
              <div className="pointer-events-none absolute inset-0" aria-hidden>
                <div className="absolute left-[10%] top-[12%] h-16 w-16 rounded-full bg-violet-300/16 blur-xl" />
                <div className="absolute right-[12%] bottom-[10%] h-14 w-14 rounded-full bg-fuchsia-300/12 blur-xl" />
              </div>
              <img
                src="/belot-button.png"
                alt=""
                className="relative z-[1] mx-auto block h-auto max-h-[72px] w-full max-w-[min(320px,92%)] origin-center object-contain object-center transition-transform duration-200 ease-out sm:max-h-[82px]"
                aria-hidden="true"
                draggable={false}
              />
            </div>

            <section className="relative w-full overflow-hidden rounded-2xl border border-violet-300/20 bg-gradient-to-b from-[#1c1530]/95 to-[#120c22]/95 p-4 shadow-[0_12px_28px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.05)] sm:p-5">
              <div className="pointer-events-none absolute inset-0 opacity-90" aria-hidden>
                <div className="absolute -left-6 top-0 h-28 w-28 rounded-full bg-violet-500/15 blur-3xl" />
                <div className="absolute -right-4 bottom-0 h-24 w-24 rounded-full bg-amber-500/10 blur-3xl" />
              </div>
              <div className="relative z-[1] flex flex-col items-stretch gap-4">
                <div className="text-center">
                  <p className="text-base font-black tracking-tight text-violet-100 sm:text-lg">
                    Локальный матч против ботов
                  </p>
                  <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-slate-400 sm:text-[13px]">
                    Командный режим 2v2: выбери правила и настрой стол перед стартом.
                  </p>
                </div>
                <div>
                  <p className="mb-2 text-center text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">
                    Правила стола
                  </p>
                  <div
                    className="grid w-full grid-cols-2 gap-2 rounded-2xl border border-white/[0.1] bg-black/35 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_10px_24px_rgba(0,0,0,0.35)]"
                    role="group"
                    aria-label="Вариант правил белота"
                  >
                    <button
                      type="button"
                      onClick={() => switchMode('classic')}
                      aria-pressed={rulesVariant === 'classic'}
                      className={cn(
                        'group relative min-h-[72px] overflow-hidden rounded-xl border px-3 py-2.5 text-left transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#120c22] active:scale-[0.985]',
                        rulesVariant === 'classic'
                          ? 'border-violet-300/60 bg-gradient-to-br from-[#4b2a86]/45 via-[#2f1d57]/55 to-[#1c1236]/75 text-violet-50 shadow-[0_0_0_1px_rgba(196,181,253,0.24),0_14px_26px_rgba(109,40,217,0.25)]'
                          : 'border-white/10 bg-white/[0.03] text-slate-300 hover:border-violet-300/30 hover:bg-violet-500/10',
                      )}
                    >
                      <span
                        className={cn(
                          'pointer-events-none absolute inset-0 opacity-0 transition duration-300',
                          rulesVariant === 'classic'
                            ? 'opacity-100 bg-[radial-gradient(circle_at_20%_0%,rgba(196,181,253,0.24),transparent_55%)]'
                            : 'group-hover:opacity-100 bg-[radial-gradient(circle_at_20%_0%,rgba(196,181,253,0.14),transparent_55%)]'
                        )}
                      />
                      <div className="relative z-[1] flex items-start gap-2.5">
                        <ModeFlag src="/Flag_of_Moldova.png" alt="MD" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <BookOpen className="h-3.5 w-3.5 shrink-0 text-violet-200/90" aria-hidden />
                            <p className="truncate text-xs font-black tracking-[0.02em] sm:text-sm">Молдавский</p>
                          </div>
                          <p className="mt-0.5 text-[10px] font-semibold text-violet-100/80 sm:text-[11px]">
                            Локально · bazar / strigare
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => switchMode('armenian')}
                      aria-pressed={rulesVariant === 'armenian'}
                      title="Базар Блот: торги 100–250, объявления на первом ходу"
                      className={cn(
                        'group relative min-h-[72px] overflow-hidden rounded-xl border px-3 py-2.5 text-left transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#120c22] active:scale-[0.985]',
                        rulesVariant === 'armenian'
                          ? 'border-amber-300/60 bg-gradient-to-br from-[#7a4a12]/45 via-[#4a2d0f]/55 to-[#2b1a0d]/75 text-amber-50 shadow-[0_0_0_1px_rgba(251,191,36,0.24),0_14px_26px_rgba(217,119,6,0.22)]'
                          : 'border-white/10 bg-white/[0.03] text-slate-300 hover:border-amber-300/30 hover:bg-amber-500/10',
                      )}
                    >
                      <span
                        className={cn(
                          'pointer-events-none absolute inset-0 opacity-0 transition duration-300',
                          rulesVariant === 'armenian'
                            ? 'opacity-100 bg-[radial-gradient(circle_at_20%_0%,rgba(252,211,77,0.22),transparent_55%)]'
                            : 'group-hover:opacity-100 bg-[radial-gradient(circle_at_20%_0%,rgba(252,211,77,0.14),transparent_55%)]'
                        )}
                      />
                      <div className="relative z-[1] flex items-start gap-2.5">
                        <ModeFlag src="/Flag_of_Armenia.png" alt="AM" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Sparkles className="h-3.5 w-3.5 shrink-0 text-amber-200/90" aria-hidden />
                            <p className="truncate text-xs font-black tracking-[0.02em] sm:text-sm">Армянский</p>
                          </div>
                          <p className="mt-0.5 text-[10px] font-semibold text-amber-100/80 sm:text-[11px]">
                            Локально · Базар Блот
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 border-t border-white/[0.06] pt-3 text-center">
                  <div className="relative flex h-16 w-16 shrink-0 items-center justify-center sm:h-[4.5rem] sm:w-[4.5rem]">
                    <div
                      className={cn(
                        'absolute inset-0 rounded-full opacity-80 blur-sm transition duration-300',
                        rulesVariant === 'classic' ? 'bg-violet-500/35' : 'bg-amber-500/30',
                      )}
                      aria-hidden
                    />
                    <div className="relative flex h-full w-full items-center justify-center rounded-full border border-white/10 bg-[#16122a]/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                      <Swords
                        className={cn(
                          'h-8 w-8 transition-colors duration-300 sm:h-9 sm:w-9',
                          rulesVariant === 'classic' ? 'text-violet-200' : 'text-amber-100',
                        )}
                        aria-hidden
                      />
                    </div>
                  </div>
                  <p className="text-[11px] font-semibold text-slate-400">
                    Выбранный режим: <span className="text-violet-100">{rulesVariant === 'classic' ? 'Молдавский' : 'Армянский'}</span>
                  </p>
                </div>
              </div>
            </section>

            <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#151227]/92 to-[#100d1e]/92 p-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] sm:p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <p className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">Параметры стола</p>
                <span className="rounded-full border border-violet-300/30 bg-violet-400/10 px-2 py-0.5 text-[10px] font-semibold text-violet-100">
                  4/4 игроков
                </span>
              </div>

              {rulesVariant === 'classic' ? (
                <>
                  <p className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    <Bot className="h-3.5 w-3.5" aria-hidden />
                    Цель матча
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[501, 1001].map((count) => (
                      <button
                        key={count}
                        type="button"
                        onClick={() => setMoldovanTargetScore(count)}
                        aria-label={`цель ${count}`}
                        className={cn(
                          'group overflow-hidden rounded-xl border px-2 py-2.5 text-sm font-bold transition min-h-[62px]',
                          moldovanTargetScore === count
                            ? 'border-violet-300/65 bg-violet-400/15 text-violet-100 shadow-[0_0_0_1px_rgba(196,181,253,0.22)]'
                            : 'border-white/10 bg-[#16122a]/70 text-slate-200 hover:border-violet-300/35 hover:bg-violet-500/10'
                        )}
                      >
                        До {count}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2.5 text-[11px] text-slate-400">
                    Матчап: <span className="font-semibold text-violet-100">{heroName || 'Игрок'}</span> vs 3 бота(ов)
                  </p>
                </>
              ) : (
                <>
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Армянские параметры</p>
                  <div className="flex gap-2">
                    {[151, 301].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setArmenianTargetScore(v)}
                        className={cn(
                          'flex-1 rounded-xl border py-2 text-sm font-bold transition',
                          armenianTargetScore === v
                            ? 'border-violet-400/70 bg-violet-500/20 text-violet-100'
                            : 'border-white/10 bg-[#16122a]/70 text-slate-300 hover:border-violet-300/35',
                        )}
                      >
                        До {v}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2" role="group" aria-label="Сложность ботов">
                    {[{ id: 'simple', label: 'Боты: простые' }, { id: 'pro', label: 'Боты: умные' }].map(({ id, label }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setArmenianBotPlayMode(id)}
                        className={cn(
                          'flex-1 rounded-xl border py-2 text-xs font-bold transition sm:text-sm',
                          armenianBotPlayMode === id
                            ? 'border-cyan-400/60 bg-cyan-500/15 text-cyan-100'
                            : 'border-white/10 bg-[#16122a]/70 text-slate-300 hover:border-cyan-300/35',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div className="mt-3 rounded-xl border border-violet-300/25 bg-black/20 p-2.5">
                <div className="mb-1.5 flex items-center justify-between gap-2 text-[11px]">
                  <span className="font-semibold uppercase tracking-wider text-slate-400">Ставка на матч</span>
                  <span className="font-black text-violet-100">{activeStake.toLocaleString('ru-RU')} фишек</span>
                </div>
                <input
                  type="range"
                  min={LOCAL_STAKE_MIN}
                  max={LOCAL_STAKE_MAX}
                  step={LOCAL_STAKE_STEP}
                  value={activeStake}
                  onChange={(e) => {
                    const next = Math.max(LOCAL_STAKE_MIN, Math.min(LOCAL_STAKE_MAX, Number(e.target.value) || LOCAL_STAKE_MIN));
                    if (rulesVariant === 'classic') setSelectedStake(next);
                    else setArmenianStake(next);
                  }}
                  className="w-full accent-violet-400"
                  aria-label="Ставка в локальном белоте"
                />
                <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-500">
                  <span>{LOCAL_STAKE_MIN.toLocaleString('ru-RU')}</span>
                  <span>{LOCAL_STAKE_MAX.toLocaleString('ru-RU')}</span>
                </div>
                <p className={cn('mt-1.5 text-[11px]', hasEnoughBalance ? 'text-slate-400' : 'text-rose-300')}>
                  Баланс: {balanceValue.toLocaleString('ru-RU')} фишек
                  {!hasEnoughBalance ? ' · Недостаточно для выбранной ставки' : ''}
                </p>
              </div>
            </div>

            <details className="rounded-2xl border border-violet-300/25 bg-gradient-to-b from-violet-500/[0.06] to-transparent px-3.5 py-3 text-xs text-slate-300 sm:px-4">
              <summary className="cursor-pointer list-none text-[11px] font-black uppercase tracking-[0.14em] text-violet-200">
                Кратко о Белоте
              </summary>
              <ul className="mt-2.5 space-y-1.5 leading-relaxed">
                <li><span className="font-semibold text-violet-100">Формат</span> — 2 на 2, всего 4 игрока</li>
                <li><span className="font-semibold text-violet-100">Старт раунда</span> — торги за козырь</li>
                <li><span className="font-semibold text-violet-100">Цель</span> — набрать больше очков, чем соперники</li>
              </ul>
            </details>
            <div className="sticky bottom-2 z-20 space-y-2 rounded-2xl border border-violet-300/25 bg-[#0f0a1e]/90 p-2.5 shadow-[0_10px_26px_rgba(0,0,0,0.35)] backdrop-blur">
              <button
                type="button"
                onClick={startSelectedMode}
                disabled={!hasEnoughBalance || economyBusy}
                className="mx-auto inline-flex w-full max-w-md items-center justify-center gap-1 rounded-2xl border border-violet-300/45 bg-gradient-to-b from-violet-500/25 to-violet-700/20 py-3 text-sm font-black text-violet-100 shadow-[0_12px_28px_rgba(139,92,246,0.22)] transition hover:border-violet-200/65 hover:from-violet-400/30 hover:to-violet-700/28"
              >
                <Play className="h-4 w-4" aria-hidden />
                НАЧАТЬ
              </button>
              {!hasJwtToken ? (
                <p className="text-center text-[11px] text-amber-200/85">
                  Для старта матча нужен вход в аккаунт (JWT).
                </p>
              ) : null}
              {(message.startsWith('Недостаточно') ||
                message.startsWith('Для локального') ||
                message.startsWith('Требуется вход') ||
                message.startsWith('Ошибка старта') ||
                message.startsWith('У вас уже активный матч')) ? (
                <div className="rounded-xl border border-rose-400/35 bg-rose-950/20 px-3 py-2 text-center text-[11px] text-rose-100">
                  {message}
                </div>
              ) : null}
              {remoteActiveSession ? (
                <button
                  type="button"
                  onClick={recoverStuckServerSession}
                  disabled={sessionRecoveryBusy || economyBusy}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-amber-300/45 bg-amber-900/25 py-2.5 text-xs font-semibold text-amber-100 transition hover:bg-amber-900/40 disabled:opacity-60"
                >
                  {sessionRecoveryBusy ? 'Завершаем зависший матч...' : 'Завершить зависший матч на сервере'}
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto flex w-full max-w-2xl flex-col gap-2 border-b border-border/35 pb-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground sm:text-xs">
                <span className="tabular-nums">
                  <span className="text-muted-foreground/75">Счёт</span>{' '}
                  <span className="font-mono font-semibold text-foreground">
                    {game?.teamScores?.[0] ?? 0} : {game?.teamScores?.[1] ?? 0}
                  </span>
                  <span className="text-muted-foreground/65"> / {BELOT_MATCH_TARGET}</span>
                </span>
                <span className="text-muted-foreground/35">·</span>
                <span className="tabular-nums">
                  <span className="text-muted-foreground/75">Взятки</span>{' '}
                  <span className="font-mono font-semibold text-foreground">
                    {teamTricks[0]} : {teamTricks[1]}
                  </span>
                </span>
                <span className="text-muted-foreground/35">·</span>
                <span className="tabular-nums text-muted-foreground/90">
                  Раунд <span className="font-mono font-medium text-foreground">{game?.roundNum ?? 1}</span>
                </span>
              </div>
              <div className="flex items-center justify-end gap-1.5 text-[9px] sm:text-[10px]">
                <span
                  className="max-w-[9.5rem] truncate rounded-md border border-violet-300/30 bg-violet-950/20 px-1.5 py-0.5 font-mono text-violet-200"
                  title={`Козырь: ${classicViewModel.contractLabel}`}
                >
                  Козырь: {classicViewModel.contractLabel}
                </span>
                {heroTurnSecondsLeft != null && myTurn ? (
                  <span className="inline-flex items-center gap-1 rounded-md border border-neon-cyan/30 bg-neon-cyan/10 px-1.5 py-0.5 font-mono tabular-nums text-neon-cyan">
                    {heroTurnSecondsLeft}s
                  </span>
                ) : null}
              </div>
            </div>

            <div className="rounded-xl border border-neon-cyan/25 bg-card/55 p-2">
              <div
                ref={seatArenaRef}
                className={cn(
                  'relative mx-auto w-full',
                  seatArena.maxWidthClass,
                  seatArena.minHeightClass,
                  seatArrangeMode && 'rounded-xl ring-2 ring-amber-400/45 ring-offset-2 ring-offset-background',
                )}
              >
                <div className="absolute inset-0 flex items-center justify-center py-0.5">
                  <PokerTableSurface
                    ref={deckRef}
                    communitySlots={[null, null, null, null, null]}
                    pot={classicViewModel.tablePot}
                    potLabel="ВЗЯТКИ"
                    showCommunity={false}
                    showDeck
                    deckPulseKey={dealVisualEpoch}
                    deckLayerExtra={
                      <BelotDeckTrumpIndicator
                        enabled
                        phase={deckTrumpPhaseClassic}
                        contract={deckTrumpContractClassic}
                        roundIndex={Math.max(0, Number(game?.roundNum || 1) - 1)}
                        auctionRevealMs={2350}
                        prefersReducedMotion={prefersReducedMotionDeckTrumpClassic}
                        cardIdToPlaying={belotCardIdToPlaying}
                      />
                    }
                  />
                </div>
                <div className="pointer-events-none absolute inset-0" style={{ zIndex: BELOT_FX_Z_TABLE }}>
                  {seatSlots.map((slot) => {
                    void oppRingTick;
                    const seatIndex = slot.seat;
                    const name = game?.currentRound?.players?.[seatIndex] || `Игрок ${seatIndex + 1}`;
                    const handSize = roundState?.handSizes?.[seatIndex] ?? 0;
                    const isHero = seatIndex === heroIndex;
                    const isTurn = classicViewModel.currentTurnSeat === seatIndex && roundState?.status === 'in_progress';
                    const botTurnFraction =
                      isTurn && !isHero && oppTurnClock
                        ? Math.max(0, Math.min(1, (oppTurnClock.endAt - Date.now()) / oppTurnClock.totalMs))
                        : undefined;
                    const allBotCards = !isHero ? seatBotCards[seatIndex] : [];
                    const cardsForRender = !isHero
                      ? (roundDealAnimActive ? allBotCards : allBotCards.slice(0, 3))
                      : [];
                    const disp = seatDisplayPercents(slot, seatOffsetBySeat[seatIndex]);
                    return (
                      <div
                        key={`seat-slot-${seatIndex}`}
                        className="absolute"
                        style={{
                          left: `${disp.leftPct}%`,
                          top: `${disp.topPct}%`,
                          transform: `translate(-50%, -50%) scale(${seatDensity.seatScale})`,
                          transformOrigin: 'center',
                        }}
                      >
                        <div
                          className={cn(
                            'flex flex-col items-center gap-0.5',
                            seatArrangeMode
                              ? 'pointer-events-auto cursor-grab touch-none active:cursor-grabbing'
                              : 'pointer-events-none',
                            roundDealAnimActive && !prefersReducedMotion && 'animate-[app-toast-in_320ms_cubic-bezier(0.2,0.8,0.2,1)]'
                          )}
                          style={
                            roundDealAnimActive && !prefersReducedMotion
                              ? { animationDelay: `${seatIndex * 55}ms`, animationFillMode: 'both' }
                              : undefined
                          }
                          onPointerDown={(ev) => handleSeatDragPointerDown(ev, seatIndex, slot.leftPct, slot.topPct)}
                          onPointerMove={handleSeatDragPointerMove}
                          onPointerUp={handleSeatDragPointerUp}
                          onPointerCancel={handleSeatDragPointerUp}
                          onLostPointerCapture={handleSeatDragLostCapture}
                        >
                          <IndustrialSeat
                            name={name}
                            stackMetaVertically
                            boostSeatAvatar
                            chipsDisplay={seatTurnMetaLabel({ handSize, roundDealAnimActive })}
                            chips={null}
                            compact={seatDensity.compact}
                            seatColorIndex={seatIndex}
                            isCurrentTurn={isTurn}
                            isUser={isHero}
                            isBot={!isHero}
                            isWinner={roundOver && handSize === 0}
                            showCards={!isHero}
                            cards={cardsForRender}
                            cardFaceDownByIndex={!isHero ? cardsForRender.map(() => true) : undefined}
                            showCardByIndex={
                              !isHero && roundDealAnimActive && seatDealSlotRevealed?.[seatIndex]
                                ? allBotCards.map((_, i) => Boolean(seatDealSlotRevealed?.[seatIndex]?.[i]))
                                : undefined
                            }
                            cardSlotRefs={!isHero ? seatCardSlotRefs[seatIndex] : undefined}
                            dealCardKeys={
                              !isHero
                                ? cardsForRender.map((_, i) => `classic-${dealVisualEpoch}-${seatIndex}-${i}`)
                                : undefined
                            }
                            turnTimeLeftSec={isHero && isTurn ? heroTurnSecondsLeft : null}
                            turnTimeTotalSec={isHero && isTurn ? BELOT_HERO_TURN_SECONDS : null}
                            turnTimeFraction={botTurnFraction}
                            suppressAvatarDragPropagation={seatArrangeMode}
                            nameActionBadge={(
                              <span className="inline-flex items-center gap-1">
                                <span
                                  className={cn(
                                    'inline-flex items-center justify-center rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide',
                                    isTurn
                                      ? 'border-cyan-300/70 bg-cyan-500/20 text-cyan-100 shadow-[0_0_12px_rgba(34,211,238,0.35)]'
                                      : 'border-white/20 bg-black/25 text-slate-300'
                                  )}
                                  title={isTurn ? 'Сейчас ход этого игрока' : 'Осталось карт'}
                                >
                                  {isTurn ? 'ХОД' : `${handSize}`}
                                </span>
                                {!isTurn ? <SeatLastMoveBadge action={lastSeatActions?.[seatIndex]} /> : null}
                              </span>
                            )}
                          >
                            {!seatDensity.hideSecondaryMeta ? (
                              <div className="mt-0.5 flex w-full items-center justify-center gap-1">
                                <span
                                  className={cn(
                                    'inline-flex max-w-full items-center justify-center rounded-md border px-2 py-0.5 text-center text-[10px] font-bold uppercase tracking-wide',
                                    seatIndex === 0 || seatIndex === 2
                                      ? 'border-emerald-500/50 bg-emerald-500/[0.12] text-emerald-300 shadow-[0_0_14px_rgba(52,211,153,0.28)]'
                                      : 'border-red-500/50 bg-red-500/[0.12] text-red-300 shadow-[0_0_14px_rgba(248,113,113,0.22)]'
                                  )}
                                >
                                  {teamLabelBySeat(seatIndex)}
                                </span>
                              </div>
                            ) : null}
                          </IndustrialSeat>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="pointer-events-none absolute inset-0 z-20">
                  {classicViewModel.trickRows.map((card, idx) => {
                    const position = getRelativeSeatPosition(card.seat, heroIndex);
                    const isWinningCard = trickView.winnerCardKey === card.key;
                    const transform = trickView.phase === 'collect'
                      ? TRICK_COLLECT_TRANSFORM[trickView.winnerPosition]
                      : TRICK_SLOT_TRANSFORM[position];
                    return (
                      <div
                        key={`${card.key}-${idx}`}
                        className={cn(
                          'absolute left-1/2 top-1/2 transition-all ease-out',
                          prefersReducedMotion ? 'duration-200' : 'duration-700',
                          trickView.phase === 'collect' ? 'opacity-0' : 'opacity-100'
                        )}
                        style={{ transform, zIndex: isWinningCard ? 20 : 10 + idx }}
                      >
                        <div className="relative">
                          <PlayingCard
                            suit={card.suit}
                            rank={card.rank}
                            size="sm"
                            className={cn(
                              'shadow-2xl',
                              trickView.phase === 'reveal' && isWinningCard && 'ring-2 ring-neon-gold ring-offset-1 ring-offset-black/35'
                            )}
                          />
                          <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black/55 px-1 py-0.5 text-[9px] text-white/80">
                            {card.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div
                  ref={trickCenterRef}
                  className="pointer-events-none absolute left-1/2 top-1/2 z-[19] h-[28%] w-[32%] -translate-x-1/2 -translate-y-1/2"
                  aria-hidden
                />
                {dragPlayCard ? (
                  <div className="pointer-events-none absolute left-1/2 top-1/2 z-[19] -translate-x-1/2 -translate-y-1/2">
                    <div
                      className={cn(
                        'h-[10.8rem] w-24 max-h-[66vmin] max-w-[48vmin] shrink-0 rounded-[50%] border-2 transition-all duration-100',
                        dragPlayCard.dropActive
                          ? 'border-cyan-300/95 bg-cyan-500/18 shadow-[0_0_28px_rgba(34,211,238,0.5)]'
                          : 'border-cyan-400/55 bg-cyan-500/10 shadow-[0_0_18px_rgba(34,211,238,0.26)]',
                      )}
                    />
                  </div>
                ) : null}
              </div>
              <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
                {trickView.phase === 'reveal' && trickView.winnerName
                  ? `Взятку забирает: ${trickView.winnerName}`
                  : cardsOnTable.length > 0
                    ? `На столе: ${cardsOnTable.length}/4`
                    : roundDealAnimActive
                      ? 'Раздача карт...'
                      : 'Ожидание следующей карты'}
              </p>
              {lastTrick && (
                <div className="mt-2 rounded-lg border border-border/60 bg-black/20 p-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-neon-gold">Последняя взятка</p>
                  <div className="mt-1 flex flex-wrap justify-center gap-1.5">
                    {lastTrick.cards.map((card, idx) => {
                      const key = `${card.rank}${card.suit}-${card.placedBy}-${idx}`;
                      const isStrongest = lastTrick.strongest
                        && card.rank === lastTrick.strongest.rank
                        && card.suit === lastTrick.strongest.suit
                        && card.placedBy === lastTrick.strongest.placedBy;
                      return (
                        <div
                          key={key}
                          className={cn(
                            'rounded-md border px-1.5 py-1 text-[10px] text-foreground',
                            isStrongest ? 'border-neon-gold/60 bg-neon-gold/10' : 'border-border/50 bg-black/20'
                          )}
                        >
                          <span className="font-semibold">{card.rank}{CARD_SUIT[card.suit] || card.suit}</span>
                          <span className="ml-1 text-muted-foreground">{card.placedBy}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <p className="text-center text-[10px] text-muted-foreground">
              Контракт: <span className="font-mono text-neon-cyan">{classicViewModel.contractLabel}</span>
            </p>

            <div className="rounded-xl border border-neon-cyan/25 bg-card/55 p-2">
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-neon-cyan">Ваша рука</p>
              <div className="flex h-[196px] items-end justify-center overflow-hidden pb-0 pt-2">
                <div
                  className="relative [filter:drop-shadow(0_0_15px_rgba(0,255,255,0.35))]"
                  style={{ width: `${fanWidth}px` }}
                >
                {classicViewModel.heroCards.some((c) => c.canPlay) && !roundDealAnimActive ? (
                  <div
                    className="pointer-events-none absolute left-1/2 bottom-0 z-[1] h-[7.35rem] w-[min(100%,19rem)] max-w-[92%] -translate-x-1/2 translate-y-1 rounded-[50%] border-2 border-neon-gold/50 bg-gradient-to-t from-neon-gold/14 via-neon-gold/5 to-transparent shadow-[0_0_32px_rgba(255,215,0,0.22)]"
                    aria-hidden
                  />
                ) : null}
                {classicViewModel.heroCards.map(({ card, idx, key, canPlay, cardId }) => {
                  const centerOffset = idx - (handCount - 1) / 2;
                  const spreadFactor = handCount > 1 ? centerOffset / ((handCount - 1) / 2) : 0;
                  const rotate = spreadFactor * 18;
                  const curveY = Math.abs(spreadFactor) * 20;
                  const liftY = hoveredCardIndex === idx && canPlay ? 22 : 0;
                  const baseScale = canPlay ? 1.2 : 1;
                  const scale = hoveredCardIndex === idx && canPlay ? 1.24 : baseScale;
                  const translateX = centerOffset * fanStep;
                  const baseZIndex = canPlay ? 80 + idx : idx + 1;
                  return (
                    <button
                      key={key}
                      ref={handSlotRefs[idx]}
                      type="button"
                      onClick={() => canPlay && onHeroCardClick(card)}
                      onPointerDown={(event) => onHeroCardPointerDown(event, card, idx, canPlay)}
                      onPointerMove={onHeroCardPointerMove}
                      onPointerUp={onHeroCardPointerUp}
                      onPointerCancel={onHeroCardPointerCancel}
                      onLostPointerCapture={onHeroCardPointerCancel}
                      onMouseEnter={() => setHoveredCardIndex(idx)}
                      onMouseLeave={() => setHoveredCardIndex(null)}
                      onFocus={() => setHoveredCardIndex(idx)}
                      onBlur={() => setHoveredCardIndex(null)}
                      disabled={!canPlay}
                      className={cn(
                        'absolute bottom-0 left-1/2 appearance-none border-0 bg-transparent p-0 transition-[transform,filter] duration-300 focus:outline-none',
                        canPlay ? 'cursor-pointer' : 'cursor-not-allowed opacity-65'
                      )}
                      style={{
                        zIndex: hoveredCardIndex === idx ? 200 : baseZIndex,
                        transform: `translateX(calc(-50% + ${translateX}px)) translateY(${fanBaseYOffset + curveY - liftY}px) rotate(${rotate}deg) scale(${scale})`,
                      }}
                      aria-label={`Сыграть ${card.rank} ${CARD_SUIT[card.suit] || card.suit}`}
                    >
                      {roundDealAnimActive ? (
                        Boolean(seatDealSlotRevealed?.[heroIndex]?.[idx]) ? (
                          <PlayingCard
                            faceDown
                            size="md"
                            className="animate-[app-toast-in_300ms_cubic-bezier(0.2,0.8,0.2,1)]"
                            style={
                              prefersReducedMotion
                                ? undefined
                                : { animationDelay: `${idx * 45}ms`, animationFillMode: 'both' }
                            }
                          />
                        ) : (
                          <div className="h-[5.5rem] w-16 shrink-0 opacity-0" />
                        )
                      ) : dragPlayCard && dragPlayCard.cardId === cardId && dragPlayCard.originIndex === idx ? (
                        <div className="h-[5.5rem] w-16 rounded-md border border-cyan-300/25 bg-cyan-500/10 opacity-35" />
                      ) : (
                        <PlayingCard
                          suit={toPlayingSuit(card.suit)}
                          rank={card.rank}
                          size="md"
                          highlighted={canPlay}
                          className={cn(
                            canPlay &&
                              'scale-100 opacity-100 ring-1 ring-neon-gold/55 shadow-[0_6px_22px_rgba(255,215,0,0.38)]',
                            !canPlay && 'grayscale-[0.1]'
                          )}
                        />
                      )}
                    </button>
                  );
                })}
                </div>
              </div>
              {!myTurn && <p className="mt-2 text-xs text-muted-foreground">Сейчас ходит другой игрок.</p>}
              {roundOver && (
                <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-neon-gold">
                  <ChevronRight className="h-3 w-3" aria-hidden />
                  Раунд завершён — нажмите «Следующий раунд».
                </p>
              )}
            </div>

            <div className="sticky bottom-0 z-30 bg-gradient-to-t from-background via-background to-transparent pb-3 pt-1.5">
              <div className="mb-2 overflow-hidden rounded-xl border border-violet-500/25 bg-slate-950/80 shadow-[0_-6px_28px_rgba(0,0,0,0.35),0_0_0_1px_rgba(139,92,246,0.12)_inset] backdrop-blur-md ring-1 ring-white/[0.04]">
                <button
                  type="button"
                  id="belot-stats-trigger"
                  aria-expanded={belotStatsOpen}
                  aria-controls="belot-stats-details"
                  onClick={() => setBelotStatsOpen((o) => !o)}
                  className={cn(
                    'relative isolate flex w-full min-w-0 items-center gap-2 overflow-hidden border-b border-white/[0.06] px-3 py-2.5 text-left text-[10px] font-medium leading-snug transition-all duration-200 sm:text-xs',
                    'bg-gradient-to-br from-violet-950/85 via-[#0c1222]/95 to-cyan-950/50',
                    'shadow-[inset_0_1px_0_rgba(255,255,255,0.07)]',
                    'hover:from-violet-900/90 hover:via-[#101a2e]/98 hover:to-cyan-950/65 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_0_24px_rgba(139,92,246,0.18)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    belotStatsOpen && 'from-violet-900/95 via-[#0f182c]/98 to-cyan-950/70'
                  )}
                >
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-[linear-gradient(110deg,rgba(167,139,250,0.14)_0%,transparent_42%,transparent_58%,rgba(34,211,238,0.1)_100%)]"
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-[0.4] mix-blend-overlay bg-[repeating-linear-gradient(-52deg,rgba(255,255,255,0.045)_0px,rgba(255,255,255,0.045)_1px,transparent_1px,transparent_7px)]"
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full bg-violet-400/15 blur-2xl"
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute -left-6 bottom-0 h-16 w-20 rounded-full bg-cyan-400/10 blur-xl"
                  />
                  <span className="relative z-[1] min-w-0 flex-1 truncate">
                    {roundState ? (
                      <>
                        <span className="font-bold uppercase tracking-wider text-violet-200/95">Сводка</span>
                        <span className="text-white/20"> · </span>
                        <span className="font-medium text-amber-100/90">
                          {roundState.suit ? SUIT_LABEL[roundState.suit] || roundState.suit : 'козырь не выбран'}
                        </span>
                        <span className="text-white/20"> · </span>
                        <span className="font-mono text-[9px] text-cyan-300/75 tabular-nums sm:text-[10px]">
                          {roundState.status}
                        </span>
                        <span className="text-white/20"> · </span>
                        <span className="text-slate-400/90">ход </span>
                        <span className="font-semibold text-cyan-200 drop-shadow-[0_0_10px_rgba(34,211,238,0.35)]">
                          {roundState.pTurnName}
                        </span>
                      </>
                    ) : (
                      <span className="text-slate-400">Нет данных раунда</span>
                    )}
                  </span>
                  {belotStatsOpen ? (
                    <ChevronUp
                      className="relative z-[1] h-4 w-4 shrink-0 text-violet-200/90 drop-shadow-[0_0_6px_rgba(167,139,250,0.5)]"
                      aria-hidden
                    />
                  ) : (
                    <ChevronDown
                      className="relative z-[1] h-4 w-4 shrink-0 text-violet-200/90 drop-shadow-[0_0_6px_rgba(167,139,250,0.45)]"
                      aria-hidden
                    />
                  )}
                </button>
                <div
                  id="belot-stats-details"
                  role="region"
                  aria-labelledby="belot-stats-trigger"
                  aria-hidden={!belotStatsOpen}
                  className={cn(
                    'border-t border-border/45 transition-[max-height,opacity] duration-200 ease-out',
                    belotStatsOpen ? 'max-h-[min(52vh,280px)] opacity-100' : 'max-h-0 border-t-0 opacity-0'
                  )}
                >
                  <div className="max-h-[min(52vh,280px)] overflow-y-auto">
                    {roundState ? (
                      <div className="grid grid-cols-1 gap-3 p-3 min-[400px]:grid-cols-2">
                        <div className="space-y-1.5 rounded-lg border border-violet-500/20 bg-violet-950/15 px-2.5 py-2 text-[10px] sm:text-xs">
                          <div className="flex items-center gap-1 font-semibold tracking-wide text-violet-200">
                            <Sparkles className="h-3 w-3 shrink-0 text-violet-300" aria-hidden />
                            Раунд
                          </div>
                          <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-muted-foreground">
                            <dt className="text-[9px] uppercase tracking-wide text-muted-foreground/80 sm:text-[10px]">Статус</dt>
                            <dd className="font-mono text-foreground/90">{roundState.status}</dd>
                            <dt className="text-[9px] uppercase tracking-wide text-muted-foreground/80 sm:text-[10px]">Козырь</dt>
                            <dd className="text-foreground/90">
                              {roundState.suit ? SUIT_LABEL[roundState.suit] || roundState.suit : 'не выбран'}
                            </dd>
                            <dt className="text-[9px] uppercase tracking-wide text-muted-foreground/80 sm:text-[10px]">Ход</dt>
                            <dd className="truncate font-medium text-neon-cyan/90">{roundState.pTurnName}</dd>
                            <dt className="text-[9px] uppercase tracking-wide text-muted-foreground/80 sm:text-[10px]">Руки</dt>
                            <dd className="font-mono tabular-nums text-foreground/90">{roundState.handSizes?.join(' · ') ?? '—'}</dd>
                            <dt className="text-[9px] uppercase tracking-wide text-muted-foreground/80 sm:text-[10px]">Очки карт</dt>
                            <dd className="font-mono tabular-nums font-semibold text-foreground/95">
                              {teamCardScores[0]} : {teamCardScores[1]}
                            </dd>
                          </dl>
                        </div>
                        <div className="space-y-1.5 rounded-lg border border-amber-500/25 bg-gradient-to-br from-amber-950/20 to-card/40 px-2.5 py-2 text-[10px] text-[#d8c487] sm:text-xs">
                          <div className="flex items-center gap-1 font-semibold tracking-wide text-neon-gold">
                            <Trophy className="h-3 w-3 shrink-0" aria-hidden />
                            Матч
                          </div>
                          <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-[#c4b59a]/90">
                            <dt className="text-[9px] uppercase tracking-wide text-[#a89070] sm:text-[10px]">Команда A</dt>
                            <dd className="font-mono tabular-nums font-semibold text-[#f5e6c8]">{game?.teamScores?.[0] ?? 0}</dd>
                            <dt className="text-[9px] uppercase tracking-wide text-[#a89070] sm:text-[10px]">Команда B</dt>
                            <dd className="font-mono tabular-nums font-semibold text-[#f5e6c8]">{game?.teamScores?.[1] ?? 0}</dd>
                            <dt className="text-[9px] uppercase tracking-wide text-[#a89070] sm:text-[10px]">Взятки</dt>
                            <dd className="font-mono tabular-nums text-[#e8dcc4]">
                              {teamTricks[0]} : {teamTricks[1]}
                            </dd>
                            <dt className="text-[9px] uppercase tracking-wide text-[#a89070] sm:text-[10px]">№ раунда</dt>
                            <dd className="font-mono tabular-nums text-[#e8dcc4]">{game?.roundNum ?? 1}</dd>
                          </dl>
                        </div>
                      </div>
                    ) : (
                      <p className="p-3 text-[10px] text-muted-foreground">Нет данных</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={startNextRound}
                  disabled={!game || !roundOver}
                  className="flex h-12 items-center justify-center gap-1 rounded-lg border border-border/70 bg-card/70 px-3 py-2 text-xs font-semibold text-foreground transition hover:border-neon-cyan/45 hover:text-neon-cyan disabled:pointer-events-none disabled:opacity-45"
                >
                  <RotateCw className="h-4 w-4" aria-hidden />
                  Следующий раунд
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleLeaveRequest();
                  }}
                  className="flex h-12 items-center justify-center gap-1 rounded-lg border border-violet-400/45 bg-violet-900/25 px-3 py-2 text-xs font-semibold text-violet-200 transition hover:bg-violet-900/40"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  В меню режима
                </button>
              </div>
            </div>
          </>
        )}
      </main>
      {dragPlayCard ? (
        <div
          className="pointer-events-none fixed z-[86]"
          style={{
            left: dragPlayCard.x,
            top: dragPlayCard.y,
            transform:
              `translate(-50%, -64%) rotate(5deg) scale(${dragPlayCard.returning ? 1 : 1.04})`,
            filter: dragPlayCard.dropActive
              ? 'drop-shadow(0 0 18px rgba(34,211,238,0.55))'
              : 'drop-shadow(0 12px 22px rgba(0,0,0,0.42))',
            opacity: 0.98,
          }}
          aria-hidden
        >
          <PlayingCard
            rank={dragPlayCard.rank}
            suit={dragPlayCard.suit}
            size="md"
            className={cn(
              'shadow-2xl',
              dragPlayCard.dropActive && 'ring-2 ring-cyan-300/80 ring-offset-2 ring-offset-black/25',
            )}
          />
        </div>
      ) : null}
      {entryDebitToast ? (
        <BelotCurrencyToast
          message={entryDebitToast}
          kind="debit"
          onClose={() => setEntryDebitToast('')}
        />
      ) : null}
      {leaveConfirmOpen ? (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/55 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-rose-400/45 bg-[#120e1f]/95 p-4 shadow-2xl">
            <p className="text-center text-sm font-black uppercase tracking-wide text-rose-100">Покинуть матч?</p>
            <p className="mt-2 text-center text-xs leading-relaxed text-slate-300">
              Выход из активного матча = техническое поражение и полная потеря ставки.
            </p>
            <p className="mt-1.5 text-center text-xs font-semibold text-rose-200">
              Потеря при выходе: -{currentPlayerStake.toLocaleString('ru-RU')} фишек
            </p>
            {leaveError ? (
              <p className="mt-2 rounded-lg border border-rose-300/45 bg-rose-900/30 px-2 py-1.5 text-center text-[11px] text-rose-100">
                {leaveError}
              </p>
            ) : null}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setLeaveConfirmOpen(false)}
                className="rounded-xl border border-white/15 py-2 text-xs font-semibold text-slate-200"
              >
                Отмена
              </button>
              <button
                type="button"
                disabled={economyBusy}
                onClick={confirmLeaveWithForfeit}
                className="rounded-xl border border-rose-400/50 bg-rose-900/40 py-2 text-xs font-bold text-rose-100 disabled:opacity-60"
              >
                {economyBusy ? 'Выход...' : `Выйти и потерять ${currentPlayerStake.toLocaleString('ru-RU')}`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function BelotLobby(props) {
  return (
    <AudioProvider>
      <BelotLobbyInner {...props} />
    </AudioProvider>
  );
}

